import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { widgetAPI, RateLimitError } from '../services/api';
import { WidgetConfig } from '../types';
import { Send, Mic, Volume2, VolumeX, Headphones, X, Sun, Moon, Clock, ArrowLeft, MessageSquarePlus, RotateCcw, Info, Paperclip, Hand, UserCheck, Loader2, Sparkles, LifeBuoy, WifiOff } from 'lucide-react';
import { APP_CONFIG } from '../config/appConfig';
import VoicePlayground from '../components/Playground/VoicePlayground';
import { createIdentitySync } from '../utils/identityPassthrough';
import type { IdentitySync } from '../utils/identityPassthrough';
import { newChatSessionId, randomHex128 } from '../utils/randomId';
import HelpCenterArticleView from '../components/Widget/HelpCenterArticleView';
import MaintenanceCard from '../components/Widget/MaintenanceCard';
import CSATModal from '../components/Widget/CSATModal';
import EmailVerificationModal from '../components/Widget/EmailVerificationModal';
import OtpVerifyBar from '../components/Widget/OtpVerifyBar';
import AgentMessageContent from '../components/shared/AgentMessageContent';
import VideoBubble from '../components/shared/VideoBubble';
import TryOnCard from '../components/Widget/TryOnCard';
import { formatServerDate, formatServerTime } from '../utils/datetime';
import { stripLookMarkers } from '../utils/lookBlocks';
import type { LookCardData } from '../utils/lookBlocks';
import type { ProductCardData } from '../utils/productBlocks';
import type { TryOnJob, TryOnRequest } from '../utils/tryon';

interface Attachment {
    s3_url: string;
    s3_key: string;
    filename: string;
    mime_type: string;
    file_size_bytes: number;
    type: string;
}

interface VideoPayload {
    url: string;
    label: string;
    thumbnail?: string;
}

interface Message {
    role: 'user' | 'agent';
    content: string;
    timestamp: string;
    attachments?: Attachment[];
    /** `'error'` (Plan-ErrorRecoveryAndLanguageDrift §3) marks a synthetic
     *  failure bubble that carries its own recovery actions. It is NEVER
     *  persisted and never arrives from the transcript — only `handleSend`'s
     *  catch mints it (plus the forward-compatible WS branch, inert until the
     *  backend tags its own error frame). */
    kind?: 'text' | 'video' | 'error';
    video?: VideoPayload;
    /** How many consecutive send attempts had failed when this bubble was
     *  minted (1 = first failure). Drives de-emphasis of the "file a ticket"
     *  action: once a retry has failed too, the agent itself is likely down,
     *  and asking it to file a ticket is the least useful of the three. */
    errorAttempt?: number;
    // Plan-HumanAgentHandoff-2026-06-09 §Customer UI — staff replies
    // are stamped `from_human: true` server-side on the canonical
    // message doc (Storm task #32). Sole discriminator for the
    // staff-vs-AI visual treatment (icon swap + label override).
    from_human?: boolean;
    // Optional per-message snapshot of the staff member's display name,
    // used as a label fallback when the conversation-level
    // `handoffStaffName` isn't populated (e.g. resume of a previously
    // released handoff). Backend may include it on the `from_human`
    // frame; absence is harmless.
    staff_name?: string;
    // Plan-WidgetStreaming Phase 3 — the backend's per-message id, present
    // only on a STREAMED bubble (WIDGET_STREAMING_ENABLED=true). It keys
    // stream_delta appends to their placeholder and lets the authoritative
    // final `{role:"agent"}` frame REPLACE that placeholder in place rather
    // than appending a duplicate. Absent on every non-streamed message, so
    // flag-off behaviour is untouched.
    message_id?: string;
    // True only while a streamed bubble is still receiving deltas. Cleared on
    // `stream_end` and on the authoritative final frame.
    streaming?: boolean;
}

interface PendingAttachment {
    file: File;
    previewUrl: string;
    type: 'image' | 'document';
    error?: string;
}

// ----- Phase 8: Shopify storefront channel + token handling ---------------
interface ChannelInfo {
    channel: 'shopify_embedded' | 'standalone';
    shop_domain?: string;
    customer_id?: string | null;
    currency?: string;
    // True when the widget SPA is running inside the Playground iframe
    // (`?source=playground`). Threaded onto the chat POST + WS so the
    // backend tags the conversation `source=playground`. Never true for
    // real customer embeds.
    is_playground?: boolean;
}

type StorefrontAction =
    | 'cart_add'
    | 'cart_update'
    | 'cart_change'
    | 'cart_clear'
    | 'cart_view'
    | 'discount_apply'
    | 'navigate'
    | 'go_to_checkout'
    | 'customer_info'
    | 'read_customer'
    | 'fetch_product';

const NAVIGATING_ACTIONS: ReadonlySet<StorefrontAction> = new Set<StorefrontAction>([
    'discount_apply',
    'navigate',
    'go_to_checkout',
]);

// Inline tokens that may appear in agent text bubbles. We strip them before
// rendering since the structured WS frame is the canonical action channel.
const STOREFRONT_TOKEN_RE =
    /\[(?:CART_ADD|CART_UPDATE|CART_CHANGE|CART_CLEAR|CART_VIEW|DISCOUNT_APPLY|NAVIGATE|GO_TO_CHECKOUT|CUSTOMER_INFO|READ_CUSTOMER|FETCH_PRODUCT)(?::[^\[\]]*)?\]/g;

// Walks a `[TOOL_RESULT:{...}]` marker with a brace-depth parser so nested
// JSON objects/arrays in the payload don't throw off the match. Mirrors the
// backend `_strip_tool_result_echo` as a client-side safety net.
function stripToolResultMarkers(text: string): string {
    if (!text || !text.includes('[TOOL_RESULT:')) return text;
    const MARKER = '[TOOL_RESULT:';
    let out = text;
    // Cap iterations defensively in case of malformed input.
    let cursor = 0;
    for (let iter = 0; iter < 32; iter++) {
        const start = out.indexOf(MARKER, cursor);
        if (start === -1) break;
        let braceStart = start + MARKER.length;
        while (braceStart < out.length && /\s/.test(out[braceStart])) braceStart++;
        if (out[braceStart] !== '{') {
            // Not a structured JSON payload — skip past this literal occurrence.
            cursor = start + MARKER.length;
            continue;
        }
        let depth = 0;
        let inString = false;
        let escaped = false;
        let braceEnd = -1;
        for (let i = braceStart; i < out.length; i++) {
            const ch = out[i];
            if (escaped) { escaped = false; continue; }
            if (inString) {
                if (ch === '\\') escaped = true;
                else if (ch === '"') inString = false;
                continue;
            }
            if (ch === '"') inString = true;
            else if (ch === '{') depth++;
            else if (ch === '}') {
                depth--;
                if (depth === 0) { braceEnd = i; break; }
            }
        }
        if (braceEnd === -1) break; // unbalanced, bail out
        let closeIdx = braceEnd + 1;
        while (closeIdx < out.length && /\s/.test(out[closeIdx])) closeIdx++;
        if (out[closeIdx] !== ']') break; // malformed — don't gnaw into following text
        out = out.slice(0, start) + out.slice(closeIdx + 1);
    }
    return out;
}

function stripStorefrontTokens(text: string): string {
    if (!text) return text;
    let out = text.replace(STOREFRONT_TOKEN_RE, '');
    out = stripToolResultMarkers(out);
    return out.replace(/\n{3,}/g, '\n\n').trim();
}

function readChannelFromUrl(): ChannelInfo {
    try {
        const params = new URLSearchParams(window.location.search);
        const isPlayground = params.get('source') === 'playground';
        const channel = params.get('channel');
        if (channel === 'shopify_embedded') {
            return {
                channel: 'shopify_embedded',
                shop_domain: params.get('shop_domain') || params.get('shop') || undefined,
                customer_id: params.get('customer_id'),
                currency: params.get('currency') || undefined,
                is_playground: isPlayground,
            };
        }
        return { channel: 'standalone', is_playground: isPlayground };
    } catch (err) {
        // ignore
    }
    return { channel: 'standalone' };
}

// Plan-ComponentEmbed (2026-05-18) — read `?embed=chat|voice` off the
// widget SPA URL. Returns the requested surface or null. This is only the
// REQUEST; activation is gated separately by
// `config.component_embed_enabled` (the security gate — a non-flagged
// agent never gets a working embedded component, not just hidden UI).
function readEmbedSurfaceFromUrl(): 'chat' | 'voice' | null {
    try {
        const e = new URLSearchParams(window.location.search).get('embed');
        return e === 'chat' || e === 'voice' ? e : null;
    } catch {
        return null;
    }
}

// Plan-TextVoicePoolBilling-2026-08.md §A4 — the backend's exhaustion signal
// for the chat channel, carried both as an HTTP 402 `detail` and as a WS 1008
// close `reason`. `text_limit_exhausted` is the CANONICAL string once the
// workspace is on the pooled Text allowance; `chat_limit_exhausted` is the
// legacy per-category string. We accept BOTH, permanently and unconditionally:
// which one arrives depends on the backend's `TEXT_POOL_BILLING_ENABLED`, and
// accepting both is what makes the deploy order back-first-safe in either
// direction (including a flag rollback). Both mean the same thing to a
// visitor — chat is out of budget — and both route to MaintenanceCard.
const isChatExhaustedReason = (reason: unknown): boolean =>
    reason === 'text_limit_exhausted' || reason === 'chat_limit_exhausted';

// ── Plan-WidgetReconnect (2026-08-13) — idle-drop recovery ────────────────
//
// THE BUG THIS FIXES. Turns travel by HTTP POST, not the WS; the WS is only
// the DELIVERY channel for the agent's reply. After ~30min idle an ALB /
// CloudFront idle timeout closes the socket with 1006 (abnormal, no close
// frame). The customer's next message still POSTs fine, the agent still
// answers, the reply still persists to Mongo — and then pushes into a dead
// socket. The customer sees their own message and then silence, with nothing
// in the UI indicating anything is wrong.
//
// Reconnect ALONE does not fix that: the reply was pushed while we were gone
// and is never re-pushed. The replay below is what actually recovers it.
const WS_RECONNECT_MAX_ATTEMPTS = 6;
const WS_RECONNECT_BASE_DELAY_MS = 1000;
const WS_RECONNECT_MAX_DELAY_MS = 15000;
// 🔴 REPLAY IS HELD — SHIPPED OFF ON PURPOSE. Do not flip this to `true`
// without a backend change first.
//
// Replay refetches the full history and appends what isn't on screen, deduped
// by `computeReplayAdditions`. The dedup compares message CONTENT, and the
// backend broadcasts one string over the WS and persists a DIFFERENT one for
// the same message: `_agent_frame` sends `cleaned` (`back/api/widget.py:2472`),
// then a safety pass rewrites the text (`:2634`–`:2643` — `resolve_video_tokens`
// → `strip_unresolved_video_tokens` → `_strip_tool_result_echo`) and only THAT
// version is persisted. So the two forms differ by design.
//
// 🚨 This is NOT closable from the frontend. `resolve_video_tokens` substitutes
// server-held queue state the browser never sees, so for any message that
// contained a video token there is no client-computable relationship between
// the WS form and the stored form. Normalising through `stripStorefrontTokens`
// on both sides closes some of the gap and none of that one.
//
// 🚨 And the blast radius is total, not marginal: the replay endpoint has no
// cursor (`ConversationDetailResponse` is `session_id · messages · created_at ·
// csat_score · ticket_id · is_ended` — a `since` param was built and REVERTED
// because multi-bubble turns share one identical `end_time`). Full-history-only
// replay means a single mismatched key duplicates the WHOLE conversation on
// EVERY reconnect.
//
// Unblock by fixing it at the source — either broadcast the same text we
// persist, or persist a `message_id` and key on that (strictly better; kills
// content matching entirely). Then flip this on.
//
// 🚨 SECOND PRECONDITION, ADDED AFTER `back 3735c83` — DO NOT FLIP THIS ON
// UNTIL THE BACKEND STOPS STAMPING RECOVERABLE DROPS AS ENDED.
//
// The server-side keepalive doubles as a liveness probe, so an idle-timeout
// drop (1006) is now DETECTED within ~30s, and the WS `finally` block stamps
// the conversation `status: "ended_by_user"` / `resolution_status:
// "user_ended"` once it is >60s old (`back/api/widget.py:4497`–`:4517`).
// Nothing ever flips it back to `active`, and POST `/chat` does not gate on
// status — so the customer reconnects and keeps chatting on a conversation the
// backend considers permanently ended.
//
// That matters HERE because `is_ended` deliberately covers `ended_by_user` as
// well as `resolved` (`widget.py:3596`–`:3599`). The replay path below reads
// `is_ended` and calls `setSessionEnded(true)`. With replay on, a customer who
// simply idled would reconnect and be locked into the ended-state UI —
// composer disabled, "Start new conversation" — in the MIDDLE of a live
// conversation. The `finally` stamp was written as an abandonment heuristic
// back when a close implied the customer was gone; with reconnect it no longer
// does.
const REPLAY_ENABLED = false;

// Full jitter on an exponential base. Jitter matters because every idle widget
// on a page behind the same LB gets dropped at the SAME instant — undamped
// backoff would reconnect them in lockstep and stampede the backend.
const wsReconnectDelay = (attempt: number): number => {
    const capped = Math.min(
        WS_RECONNECT_MAX_DELAY_MS,
        WS_RECONNECT_BASE_DELAY_MS * Math.pow(2, attempt),
    );
    return Math.round(capped / 2 + Math.random() * (capped / 2));
};

/**
 * Merge a replayed server transcript into what is already on screen, returning
 * ONLY the messages that are genuinely new (in server order).
 *
 * 🚨 IDEMPOTENT BY MESSAGE IDENTITY, NOT BY COUNT. Re-rendering a bubble the
 * customer can already see is a visible bug, and a count/index cursor drifts
 * the moment the two lists disagree in length — which they routinely do, since
 * the replay endpoint filters `[TOOL_RESULT:` rows and video bubbles serialize
 * with empty content and get dropped below.
 *
 * 🚨 WHY IDENTITY IS `(role, content)` AND NOT AN ID OR A TIMESTAMP — verified
 * against shipped backend code, do not "improve" this without re-checking:
 *   • `message_id` is STREAMING-ONLY AND EPHEMERAL. It is minted in the WS path
 *     (`back/api/widget.py:2216`) and never written to Mongo;
 *     `ConversationMessage` (`widget.py:664`) does not declare it. It does not
 *     exist on a replayed message, so it cannot key this merge.
 *   • The WS frame's timestamp is `datetime.utcnow()` taken at BROADCAST
 *     (`_agent_frame`, `widget.py:2244`); the persisted value is `end_time`
 *     (`widget.py:2653`) — a different instant. WS timestamp ≠ Mongo timestamp.
 *   • Every bubble in a multi-bubble turn shares ONE identical `end_time`, so a
 *     timestamp is not unique even within Mongo.
 * A multiset diff on `(role, content)` is therefore the strongest identity the
 * shipped contract actually supports. It is order-preserving for the appended
 * tail and counts duplicates PER IDENTITY, so an agent that legitimately says
 * the same sentence twice keeps both bubbles.
 *
 * (The durable fix is a persisted `message_id` reused by `_agent_frame`; that
 * is a backend change and is tracked separately.)
 */
export function computeReplayAdditions(
    rendered: { role: string; content: string }[],
    server: { role: string; content: string }[],
): { role: string; content: string }[] {
    // Multiset of what the customer can already see, keyed by identity.
    const seen = new Map<string, number>();
    // 🚨 BOTH SIDES ARE NORMALISED THROUGH THE SAME TRANSFORM, AND IT HAPPENS
    // *HERE*, NOT AT THE CALL SITES.
    //
    // `messages` state holds MIXED forms: bubbles delivered over the WS are
    // stored RAW, while bubbles loaded from history are stored ALREADY
    // STRIPPED (`stripStorefrontTokens` is applied on ingest by the history /
    // load-session paths, never by the live WS path). The server replay is
    // always the stripped form. So comparing a raw rendered bubble against a
    // stripped server one never matches, and every LIVE-delivered message —
    // the majority of any active conversation, not an edge case — would be
    // appended a second time on every reconnect.
    //
    // Normalising inside `keyOf` rather than at the two call sites is
    // deliberate: it makes the function correct for ANY caller, so a future
    // caller that passes raw content cannot silently reintroduce this. Safe to
    // apply to already-stripped text because `stripStorefrontTokens` is
    // idempotent — one pass leaves no storefront tokens, no `[TOOL_RESULT:`
    // markers and no run of 3+ newlines. It subsumes the `.trim()` this did
    // before (that trim is the last step of the transform).
    //
    // ⚠️ Card markers (`[PRODUCT_CARD:…]`, `[HELP_CENTER_CARD:…]`) are
    // deliberately NOT part of this key. They are stripped at DISPLAY time into
    // throwaway locals and remain present in state on both sides, so they
    // already match each other. If that stripping ever moves to ingest, they
    // MUST be added here — otherwise this exact bug comes back.
    //
    // The field separator is a NUL, written as an ESCAPE — never as a literal
    // NUL byte, which would make this source a binary file to git/grep/eslint.
    // A NUL can never occur in message text, so no (role, content) pair can be
    // spoofed into colliding with a different one the way a printable
    // separator would allow.
    const keyOf = (m: { role: string; content: string }) =>
        `${m.role}\u0000${stripStorefrontTokens(m.content || '')}`;
    for (const m of rendered) {
        const k = keyOf(m);
        seen.set(k, (seen.get(k) || 0) + 1);
    }
    const additions: { role: string; content: string }[] = [];
    for (const m of server) {
        const k = keyOf(m);
        const remaining = seen.get(k) || 0;
        if (remaining > 0) {
            // Already on screen — consume one occurrence and skip it.
            seen.set(k, remaining - 1);
            continue;
        }
        additions.push(m);
    }
    return additions;
}

const PERSISTED_SESSION_KEY = 'prodagi_widget_session';
const PERSISTED_SESSION_TTL_MS = 10 * 60 * 1000; // 10 minutes

interface PersistedSession {
    session_id: string;
    visitor_id: string;
    agent_id: string;
    channel: string;
    shop_domain?: string;
    customer_id?: string | null;
    ts: number;
}

function persistSession(s: PersistedSession) {
    try {
        localStorage.setItem(PERSISTED_SESSION_KEY, JSON.stringify(s));
    } catch (err) {
        // quota / private mode — ignore
    }
}

function readPersistedSession(agentId: string): PersistedSession | null {
    try {
        const raw = localStorage.getItem(PERSISTED_SESSION_KEY);
        if (!raw) return null;
        const parsed: PersistedSession = JSON.parse(raw);
        if (!parsed || parsed.agent_id !== agentId) return null;
        if (Date.now() - (parsed.ts || 0) > PERSISTED_SESSION_TTL_MS) {
            localStorage.removeItem(PERSISTED_SESSION_KEY);
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

function clearPersistedSession() {
    try {
        localStorage.removeItem(PERSISTED_SESSION_KEY);
    } catch {
        // ignore
    }
}
// --------------------------------------------------------------------------

const getContrastColor = (hexcolor: string) => {
    if (!hexcolor || hexcolor.length < 7) return 'white';
    // Remove # if present
    const hex = hexcolor.replace('#', '');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const yiq = ((r * 299) + (g * 587) + (114 * b)) / 1000;
    return (yiq >= 128) ? '#111827' : 'white'; // #111827 is gray-900
};

function playNotificationChime() {
    try {
        const ctx = new AudioContext();
        const gainNode = ctx.createGain();
        gainNode.connect(ctx.destination);
        gainNode.gain.setValueAtTime(0.25, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);

        [880, 660].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.12);
            osc.connect(gainNode);
            osc.start(ctx.currentTime + i * 0.12);
            osc.stop(ctx.currentTime + i * 0.12 + 0.4);
        });
    } catch {
        // Silently ignore — audio is non-critical
    }
}

const Widget: React.FC = () => {
    // Widget route neutralises the app shell (index.css sets body to a purple
    // gradient with min-height:100vh, and #root to min-height:100vh). Inside the
    // embed iframe that gradient leaks through any transparent area and the
    // viewport-based min-height resolves against the host page on mobile. The
    // Widget root containers below use `fixed inset-0` so their own sizing is
    // independent of the document chain, but we still clear body background and
    // pin the chain so nothing from the app shell bleeds through behind them.
    useEffect(() => {
        const html = document.documentElement;
        const body = document.body;
        const root = document.getElementById('root');
        const prev = {
            htmlHeight: html.style.height,
            bodyHeight: body.style.height,
            bodyMinHeight: body.style.minHeight,
            bodyBackground: body.style.background,
            rootHeight: root?.style.height ?? '',
            rootMinHeight: root?.style.minHeight ?? '',
            rootBackground: root?.style.background ?? '',
        };
        html.style.height = '100%';
        body.style.height = '100%';
        body.style.minHeight = '0';
        body.style.background = 'transparent';
        if (root) {
            root.style.height = '100%';
            root.style.minHeight = '0';
            root.style.background = 'transparent';
        }
        return () => {
            html.style.height = prev.htmlHeight;
            body.style.height = prev.bodyHeight;
            body.style.minHeight = prev.bodyMinHeight;
            body.style.background = prev.bodyBackground;
            if (root) {
                root.style.height = prev.rootHeight;
                root.style.minHeight = prev.rootMinHeight;
                root.style.background = prev.rootBackground;
            }
        };
    }, []);
    const { agentId } = useParams<{ agentId: string }>();
    const [config, setConfig] = useState<WidgetConfig | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    // Plan-WidgetProgressStages — live process-stage label streamed by the
    // backend during a long agent turn (e.g. "Analyzing your photo…",
    // "Curating your looks…"). Purely additive: when null, the typing bubble
    // shows the plain three dots exactly as before. Cleared on turn end /
    // handoff / new send so a stale stage never lingers.
    const [statusStage, setStatusStage] = useState<string | null>(null);
    const [sessionId, setSessionId] = useState<string>('');
    const [visitorId, setVisitorId] = useState<string>('');
    const [isAutoReadEnabled, setIsAutoReadEnabled] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [mode, setMode] = useState<'chat' | 'voice' | 'history' | 'history-detail'>('chat');
    const [historyList, setHistoryList] = useState<{ session_id: string; created_at: string; message_count: number; preview: string }[]>([]);
    const [historyMessages, setHistoryMessages] = useState<Message[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historySessionId, setHistorySessionId] = useState<string>('');
    const [historyCsatScore, setHistoryCsatScore] = useState<number | null>(null);
    const [historyTicketId, setHistoryTicketId] = useState<string | null>(null);
    const [showInfoPanel, setShowInfoPanel] = useState(false);
    const [rateLimitUntil, setRateLimitUntil] = useState<number>(0);
    const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
    // Virtual Try-On (Phase 3) — poll-based. `personImageS3` caches the
    // shopper's uploaded photo for the whole session (upload once, try on
    // many); `tryOnJobs` holds the in-flight + completed try-on cards. A
    // transient banner (`tryOnNotice`) surfaces pool-exhausted / global
    // errors. Buttons are gated upstream; this state is only ever populated
    // when VTO is enabled.
    const [personImageS3, setPersonImageS3] = useState<string | null>(null);
    const [tryOnJobs, setTryOnJobs] = useState<TryOnJob[]>([]);
    const [tryOnNotice, setTryOnNotice] = useState<string | null>(null);
    const [articleViewSlug, setArticleViewSlug] = useState<string | null>(null);
    // sessionEpoch bumps on every widget reopen signal from the parent
    // launcher (widget.js postMessage 'prodagi_widget_open'). The chat WS
    // useEffect depends on this so the WS reconnects after a close-on-
    // launcher path: close drops the socket, reopen bumps the epoch, the
    // effect re-fires with the same sessionId, a fresh WS connects.
    // Without the bump, sessionId would be unchanged and the effect
    // wouldn't re-run — leaving the user with a dead socket but a live UI.
    const [sessionEpoch, setSessionEpoch] = useState<number>(0);
    // Plan-EndConversationUX §B.1 — flipped true when the chat WS
    // receives a `conversation_ended` frame OR when the resume-from-
    // history endpoint reports `is_ended: true` on the loaded session.
    // While true: chat input + send + mic + attachment buttons are
    // disabled, an inline banner explains the state, and a
    // "Start new conversation" CTA bumps sessionEpoch + mints a fresh
    // sessionId via `handleNewChat()`. Cleared on session reset.
    const [sessionEnded, setSessionEnded] = useState<boolean>(false);
    // Plan-EffectiveLimitsAndMaintenanceBanners.md §2b (2026-05-15) —
    // local "chat tab is in maintenance" state. Initial value tracks
    // `config.chat_available` once config loads (`false` flips banner on);
    // ALSO flipped true synchronously when the chat WS closes with code
    // 1008 reason `chat_limit_exhausted` / `text_limit_exhausted` (see
    // `isChatExhaustedReason`) so the user doesn't need to
    // refetch config to see the maintenance card. Cleared on a fresh
    // session reset (handleNewChat) — though the next config fetch will
    // still drive it back to true if the budget is genuinely exhausted.
    const [chatLimitExhausted, setChatLimitExhausted] = useState<boolean>(false);

    // Plan-HumanAgentHandoff-2026-06-09 — per-conversation human handoff
    // status. Backend pushes `human_handoff_status` WS frames on state
    // transitions (none → requested → active → released). Resume payload
    // also carries the current status so a reload mid-handoff doesn't
    // lose the banner. `staffName` is the snapshot from
    // `users.full_name` at takeover time.
    const [handoffStatus, setHandoffStatus] = useState<'none' | 'requested' | 'active' | 'released'>('none');
    const [handoffStaffName, setHandoffStaffName] = useState<string | null>(null);
    const [handoffRequesting, setHandoffRequesting] = useState<boolean>(false);
    const [handoffCancelling, setHandoffCancelling] = useState<boolean>(false);
    // Plan-TalkToHumanToggleSchedule.md — transient notice shown when the
    // server rejects a human request (403 disabled / 409 outside-hours). Lets
    // us surface the off-hours/offline message instead of failing silently
    // even if a stale widget still showed the button.
    const [handoffNotice, setHandoffNotice] = useState<string | null>(null);
    // Plan-CSATUIModal.md §B.2 (2026-05-15) — CSAT modal state.
    // `csatModalOpen` flips true on `conversation_ended` when the
    // agent's `ask_for_csat` config is true AND we haven't already
    // shown the modal for this conversation. `csatSubmittedForConv`
    // remembers which session_id the user already responded to so
    // double-fired `conversation_ended` events (backend retry, WS
    // reconnect race) don't re-pop the modal after the user submitted
    // or skipped.
    const [csatModalOpen, setCsatModalOpen] = useState<boolean>(false);
    const [csatSubmittedForConv, setCsatSubmittedForConv] = useState<string | null>(null);
    // Plan-CSATUIModal.md §B.3 follow-up (2026-05-15) — which
    // conversation_id the CSAT modal POSTs to. Chat mode uses
    // Widget.tsx's `sessionId`; voice mode uses the voice WS's minted
    // `session-<random>` id (received via the
    // `onConversationEnded(conversationId)` callback). They differ
    // because VoicePlayground L243 mints its own id — Storm's CSAT
    // endpoint 404s if we send the wrong one ("not found").
    const [csatConversationId, setCsatConversationId] = useState<string | null>(null);
    // Contract-WidgetEmailOTP.md — email OTP gate. The modal opens ONLY on the
    // `verification_required` flag in the chat payload; never on agent text and
    // never on a message marker (the agent is not part of this flow at all).
    const [verifyModalOpen, setVerifyModalOpen] = useState<boolean>(false);
    // 🚨 Which session id to verify AGAINST. The backend keys verification on
    // `otp:verified:{session_id}`, and VoicePlayground mints its own
    // `session-<random>` id — so verifying under the chat `sessionId` would NOT
    // unlock a tool called during a voice turn. Null ⇒ chat, use `sessionId`.
    const [verifySessionId, setVerifySessionId] = useState<string | null>(null);
    // The question that tripped the gate. Re-sent verbatim once the visitor
    // verifies, so they never have to retype it. A ref, not state: the WS
    // handler closes over stale state, and this must survive the modal's life.
    const pendingVerifyQuestionRef = useRef<string | null>(null);
    // The address this session has already proven. FE mirror of the server's
    // `otp:verified:{session_id}` — NOT a gate, and never consulted before
    // showing data (the tool is the only gate). It exists so the UI can stop
    // asking a question the visitor already answered: it drives the transcript
    // affirmation, and the modal's reopen copy.
    const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
    // Set when the visitor dismisses the modal without verifying. Today that is
    // a dead end — the gate is server-side so nothing breaks, but the visitor
    // has no way back other than re-asking and hoping the gate trips again.
    // Drives the "Verify your email" bar above the input.
    const [verifyDismissed, setVerifyDismissed] = useState<boolean>(false);
    // `verification_reason` off the frame. 🚨 `different_email` and
    // `order_lookup` need OPPOSITE copy — see EmailVerificationModal's `reason`
    // prop. Defaults to `order_lookup`, which is both the backend default and
    // today's behaviour, so a server that never sends the field (and the voice
    // path, which still hardcodes `order_lookup`) is unaffected.
    const [verifyReason, setVerifyReason] = useState<'order_lookup' | 'different_email'>('order_lookup');
    // Identity passthrough — the partner host page's signed identity token,
    // delivered by widget.js (`prodagi_widget_identify`). 🚨 A bearer
    // credential: held in a REF only — never state (it would sit in React
    // DevTools), never rendered, never logged, never persisted. The revision
    // counter is what re-runs the verify effect; it carries no token.
    const identityTokenRef = useRef<string | null>(null);
    const [identityRevision, setIdentityRevision] = useState<number>(0);
    // Chat's own queue. Voice keeps a separate one in VoicePlayground because
    // it verifies under its own minted session id.
    const chatIdentitySyncRef = useRef<IdentitySync | null>(null);
    if (!chatIdentitySyncRef.current) chatIdentitySyncRef.current = createIdentitySync();
    // The last email-looking thing the visitor typed into the CHAT box. The
    // agent asks for the email in prose, so answering in chat is the natural
    // reading of what it asked — this prefills the modal with that answer
    // instead of demanding it a second time in a different box.
    const chatTypedEmailRef = useRef<string | null>(null);
    // What the OTP dialog should open with. Three cases, and the prefill has to
    // agree with the copy the modal shows or the two contradict each other:
    //
    //  - `different_email` ⇒ the confirmed address is the one that just FAILED.
    //    🚨 Never hand it back — offering it invites the retry that loops. An
    //    address typed in chat since is a real candidate, but only if it isn't
    //    the confirmed one wearing a different hat.
    //  - lapsed window (`order_lookup` + an address already confirmed) ⇒ the
    //    modal says "your confirmation of X has expired", so X must be what is
    //    in the box. A chat-typed address must NOT win here.
    //  - first time ⇒ whatever they typed in chat, if anything.
    const _verifyPrefill = (() => {
        const typed = chatTypedEmailRef.current;
        if (verifyReason === 'different_email') {
            return typed && typed.toLowerCase() !== (verifiedEmail || '').toLowerCase()
                ? typed
                : '';
        }
        return verifiedEmail || typed || '';
    })();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    // Virtual Try-On (Phase 3) refs: a dedicated hidden file input for the
    // person photo, the per-job poll intervals (cleared on session reset /
    // unmount), and the request awaiting a just-uploaded photo.
    const tryOnFileInputRef = useRef<HTMLInputElement>(null);
    const tryOnPollsRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});
    const pendingTryOnRef = useRef<Omit<TryOnRequest, 'person_image_s3' | 'agent_id'> & { localId: string } | null>(null);
    const ws = useRef<WebSocket | null>(null);
    // Plan-EnterpriseUpsellViaWidget §A.1 — stable ref so the
    // window.message listener (declared once with []-deps) can call the
    // latest handleSend closure when the parent page postMessages
    // `prodagi_widget_send_message`. Synced on every render via the
    // assignment below the handleSend definition.
    const handleSendRef = useRef<((text?: string) => Promise<void>) | null>(null);
    // Phase 8: Shopify channel + parent-bridge state
    const channelInfoRef = useRef<ChannelInfo>(readChannelFromUrl());
    // Latest visitorId for the empty-deps window 'message' handler (load_session).
    // The handler closure would otherwise capture the mount-time '' and never load.
    const visitorIdRef = useRef<string>('');
    // Plan-ComponentEmbed — requested embed surface (URL is constant for
    // the iframe's lifetime, mirror the channelInfoRef pattern).
    const embedSurfaceRef = useRef<'chat' | 'voice' | null>(readEmbedSurfaceFromUrl());
    // Plan-VoiceWidgetRevisions Item 2 (2026-05-25) — are we running inside
    // a `widget.js` parent iframe (a real customer embed)? True iff we're in
    // an iframe AND not the Playground preview. The Playground also embeds
    // this SPA in an iframe but has NO widget.js to widen, so it must use
    // the overlay article reader. A direct top-level `/widget/:id` tab has
    // `window.parent === window` → false → overlay too. Window relationship
    // is constant for the iframe lifetime, so compute once into a ref.
    const isEmbeddedInWidgetJs = useRef<boolean>(
        typeof window !== 'undefined' &&
        window.parent !== window &&
        readChannelFromUrl().is_playground !== true,
    ).current;
    const pendingActionsRef = useRef<Map<string, (msg: any) => void>>(new Map());
    // Tracks whether the WebSocket delivered the agent messages for the current turn.
    // If true, we skip the HTTP response messages to avoid duplicates.
    const wsDeliveredRef = useRef<boolean>(false);
    // Consecutive `POST /widget/chat` failures (Plan-ErrorRecoveryAndLanguageDrift
    // §3). Reset the moment a POST is accepted — it counts failures to SEND, not
    // failures to answer.
    const consecutiveSendFailuresRef = useRef<number>(0);
    const preferredVoiceRef = useRef<SpeechSynthesisVoice | null>(null);

    // ── Plan-WidgetReconnect (2026-08-13) ────────────────────────────────
    // 🚨 THE CORRECTNESS CRUX: which closes we must NOT reconnect after.
    //
    // The auto-disconnect on conversation resolve is a PROTECTED feature.
    // Reconnecting after it would resurrect a conversation the agent
    // deliberately ended. Critically, that teardown is CLIENT-initiated: the
    // server does not close the socket, it broadcasts a `conversation_ended`
    // frame (`back/agents/unified_agent.py:1190`) and the handler below calls
    // `ws.current.close()` itself. A client-initiated close surfaces as 1005 /
    // 1000, which is NOT reliably separable from an abnormal drop by code
    // alone — so we do not try. Every deliberate close sets this flag FIRST,
    // and the close handler treats the flag as authoritative. Intent we
    // recorded ourselves beats a code we have to interpret.
    const intentionalCloseRef = useRef<boolean>(false);
    const reconnectAttemptsRef = useRef<number>(0);
    // 🚨 Set by the FIRST frame received on the current socket, not by `onopen`.
    // The server accepts the socket before its setup finishes, so `onopen` fires
    // for a connection that may die a millisecond later — resetting the retry
    // budget there made the budget unspendable and `connState: 'lost'`
    // unreachable. A received frame is the only proof the peer is really there.
    const connAckedRef = useRef<boolean>(false);
    const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Latest sessionEnded for the WS closures, which are created once per
    // [sessionId, sessionEpoch] and would otherwise capture a stale `false`
    // — the exact way a resolved conversation could get resurrected.
    const sessionEndedRef = useRef<boolean>(false);
    // Set the moment we lose a socket abnormally; consumed on the next
    // successful open. An explicit "replay is owed" flag rather than inferring
    // it from the attempt counter, so the MANUAL retry (which resets that
    // counter) still replays — the manual path is the one a customer reaches
    // after the longest outage, i.e. the one that most needs the missed reply.
    const replayOwedRef = useRef<boolean>(false);
    // Lets the retry button reach `connect()`, which is scoped to the WS effect.
    const manualRetryRef = useRef<(() => void) | null>(null);
    // 'live' → normal. 'retrying' → a quiet inline affordance. 'lost' → retries
    // exhausted, offer a manual retry. NEVER blocks the composer, and never
    // tells the customer to refresh: PERSISTED_SESSION_TTL_MS is 10 minutes, so
    // after a 30-minute idle a refresh mints a NEW session and silently
    // destroys the very thread we are recovering.
    const [connState, setConnState] = useState<'live' | 'retrying' | 'lost'>('live');
    // Mirror `sessionEnded` into the ref the WS close handler reads. That
    // handler is built once per [sessionId, sessionEpoch] and would otherwise
    // capture the mount-time `false` — a stale read there is precisely how a
    // resolved conversation would get reconnected. Assigned during render (not
    // in an effect) so it is already correct when a close fires.
    sessionEndedRef.current = sessionEnded;

    const [theme, setTheme] = useState<'light' | 'dark'>(() => {
        if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'dark';
        }
        return 'light';
    });

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e: MediaQueryListEvent) => {
            setTheme(e.matches ? 'dark' : 'light');
        };
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    // Sync theme with parent window (launcher)
    useEffect(() => {
        if (window.parent) {
            window.parent.postMessage({ type: 'prodagi_theme_change', theme }, '*');
        }
    }, [theme]);

    // Keep visitorIdRef current for the empty-deps 'message' handler below.
    useEffect(() => { visitorIdRef.current = visitorId; }, [visitorId]);

    useEffect(() => {
        // Initialize Visitor ID
        let vid = localStorage.getItem('prodagi_ai_visitor_id');
        if (!vid) {
            // 🚨 The visitor id is a capability too: `getHistory(agentId, visitorId)`
            // lists this visitor's conversations — i.e. their session ids. Minted
            // ONLY when absent: an existing stored id is kept, never regenerated,
            // or every returning visitor would lose their history.
            vid = randomHex128();
            localStorage.setItem('prodagi_ai_visitor_id', vid);
        }
        setVisitorId(vid);

        // Phase 8: if the user just came back from a Shopify /checkout or
        // /discount redirect, resume the prior session instead of starting a new one.
        const resumed = agentId ? readPersistedSession(agentId) : null;
        if (resumed) {
            setSessionId(resumed.session_id);
            const resumedVisitorId = resumed.visitor_id || vid;
            if (resumed.visitor_id) {
                setVisitorId(resumed.visitor_id);
            }
            // Keep channel info in sync if the URL dropped it
            if (resumed.channel === 'shopify_embedded' && channelInfoRef.current.channel !== 'shopify_embedded') {
                channelInfoRef.current = {
                    channel: 'shopify_embedded',
                    shop_domain: resumed.shop_domain,
                    customer_id: resumed.customer_id,
                };
            }
            // Fetch prior conversation so the user sees their full history on
            // auto-open after a storefront navigate. Strip storefront tokens +
            // drop empty messages the same way loadConversation does.
            widgetAPI.getConversation(resumed.session_id, resumedVisitorId, channelInfoRef.current.is_playground === true)
                .then((resp) => {
                    const cleaned: Message[] = [];
                    for (const m of (resp.data.messages || []) as any[]) {
                        const stripped = stripStorefrontTokens(m.content || '');
                        const atts = m.attachments;
                        if (!stripped && !(atts && atts.length)) continue;
                        cleaned.push({
                            role: m.role as 'user' | 'agent',
                            content: stripped,
                            timestamp: m.timestamp,
                            attachments: atts,
                            // Honor backend hint when present (per
                            // Plan-HumanAgentHandoff §Data model — staff
                            // replies are tagged on the doc; the
                            // optional per-message staff_name lets a
                            // released conversation's history label
                            // each bubble with the correct name even
                            // after the conversation-level handoff
                            // metadata clears).
                            from_human: m.from_human === true ? true : undefined,
                            staff_name: typeof m.staff_name === 'string' ? m.staff_name : undefined,
                        });
                    }
                    if (cleaned.length) setMessages(cleaned);
                    // Plan-EndConversationUX §B.4 — Storm's history
                    // endpoint adds `is_ended: bool`. When true, flip
                    // straight into the ended-state UI on first paint;
                    // the chat-WS useEffect still runs and opens a
                    // socket, but the server's resume gate (Plan §A.2)
                    // immediately closes it after sending another
                    // `conversation_ended` frame. Setting the flag here
                    // saves a UI flash before that arrives.
                    if ((resp.data as { is_ended?: boolean }).is_ended === true) {
                        setSessionEnded(true);
                    }
                    // Plan-HumanAgentHandoff-2026-06-09 §Risks (WebSocket
                    // stale state) — restore handoff state from the
                    // resume payload so a customer who refreshes mid-
                    // handoff doesn't briefly see the AI-mode UI before
                    // the first WS frame lands. Backend includes the
                    // canonical conversations.human_handoff_* fields on
                    // the resume response.
                    const resumePayload = resp.data as {
                        human_handoff_status?: 'none' | 'requested' | 'active' | 'released';
                        human_handoff_by_name?: string | null;
                    };
                    if (resumePayload.human_handoff_status) {
                        setHandoffStatus(resumePayload.human_handoff_status);
                        setHandoffStaffName(resumePayload.human_handoff_by_name || null);
                    }
                })
                .catch((err) => {
                    console.warn('[Prodagi] failed to restore history for resumed session:', err);
                });
            clearPersistedSession();
        } else {
            // Pre-generate sessionId NOW so the WebSocket can connect before the
            // user sends their first message. Without this, the WS only connects
            // after the first HTTP response, which is already too late to stream.
            // 🚨 Full-entropy (128-bit CSPRNG) — the id is a capability; see utils/randomId.ts.
            const newSessionId = newChatSessionId();
            setSessionId(newSessionId);
        }

        // Load Config
        if (agentId) {
            loadConfig(agentId);
        }
    }, [agentId]);

    useEffect(() => {
        if (!sessionId) return;

        const wsUrl = (import.meta.env.VITE_API_URL || 'ws://localhost:8000').replace(/^http/, 'ws');

        // Plan-WidgetReconnect — a fresh mount/epoch is a fresh connection
        // lifecycle, so clear any intent/attempt state left by the previous one.
        intentionalCloseRef.current = false;
        reconnectAttemptsRef.current = 0;
        connAckedRef.current = false;

        // The handlers are built ONCE here and reattached by `connect()` on
        // every reconnect, so a recovered socket behaves identically to the
        // original one — no divergent second code path to keep in sync.
        const handleMessage = (event: MessageEvent) => {
            // First frame on this socket = the connection is genuinely usable
            // (see `connAckedRef`). Deliberately before the JSON.parse, so even
            // a frame we cannot read still counts as proof of life. Any frame
            // qualifies — `ws_ready`, the 30s keepalive, or a real reply — so
            // this also works against a backend that predates `ws_ready`.
            if (!connAckedRef.current) {
                connAckedRef.current = true;
                reconnectAttemptsRef.current = 0;
                setConnState('live');
                // 🔴 STILL HELD — read the comment on `REPLAY_ENABLED` before
                // wiring this on. Replay belongs on a CONFIRMED recovery, which
                // is exactly what this branch now means.
                if (REPLAY_ENABLED && replayOwedRef.current) {
                    replayOwedRef.current = false;
                    void replayMissedMessages();
                }
            }

            const data = JSON.parse(event.data);

            // Liveness frames carry no user-visible payload — swallow them
            // FIRST and silently.
            //
            // 🚨 This is deliberately forward-compatible with a keepalive the
            // backend does not emit yet. Storm's finding (QA
            // `widget-ws-keepalive-server-side-still-missing`) is that the
            // server-side keepalive is blocked on this file: unknown frames
            // fall through to the bottom handler. They no longer CRASH there
            // (the `typeof data.content !== 'string'` guard catches them), but
            // they do log a warning per frame. Naming the types here means the
            // backend can start emitting a keepalive without needing a second,
            // synchronised frontend deploy — the ordering trap that makes
            // two-sided changes fail.
            // `ws_ready` is the server's "setup finished" frame (pinned with
            // Storm). It carries no payload — its only job is the ack above.
            if (
                data?.type === 'keepalive' || data?.type === 'ping' ||
                data?.type === 'pong' || data?.type === 'ws_ready'
            ) {
                return;
            }

            // Contract-WidgetEmailOTP.md — the gate signal. Emitted when a
            // gated customer/order tool was called without a verified email.
            // Deliberately NOT a `return`: the flag rides ALONGSIDE a normal
            // payload, so we open the modal and let the frame fall through to
            // its usual handler (the agent's "I need to confirm it's you"
            // reply still renders behind the modal). Absent/false ⇒ untouched
            // behaviour, which is what keeps this additive.
            if (data.verification_required === true) {
                // 🚨 Verify against the session id the BACKEND names in the
                // frame, never one we infer locally. The gate keys on
                // `otp:verified:{session_id}`; if our id and its id disagree,
                // the visitor enters a correct code, the modal closes happily,
                // and the tool stays locked — a silent failure. Local
                // `sessionId` is only a fallback for a server that predates
                // the field.
                setVerifySessionId(
                    typeof data.session_id === 'string' && data.session_id
                        ? data.session_id
                        : null,
                );
                // 🚨 Read the reason EVERY time, including the absent case.
                // Leaving a stale `different_email` in place after a later
                // `order_lookup` would tell a visitor whose window merely
                // lapsed to go find another address — the same wrong-escape
                // failure as before, just pointing the other way.
                setVerifyReason(
                    data.verification_reason === 'different_email'
                        ? 'different_email'
                        : 'order_lookup',
                );
                setVerifyModalOpen(true);
                // A fresh gate hit supersedes an earlier dismissal — the bar
                // above the input would otherwise sit there alongside the
                // modal it opens.
                setVerifyDismissed(false);
            }

            // Typing signal from the AI agent
            if (data.type === 'typing') {
                setLoading(data.status);
                // typing:false ends the turn — drop any process stage so it
                // doesn't linger into the next turn.
                if (!data.status) setStatusStage(null);
                return;
            }

            // Plan-WidgetProgressStages — additive live process-stage label.
            // Shown as a small muted line above the typing dots while loading.
            // An old FE simply ignores this type (the dots stay plain).
            if (data.type === 'status') {
                if (typeof data.stage === 'string' && data.stage.trim()) {
                    setStatusStage(data.stage);
                }
                return;
            }

            // Plan-HumanAgentHandoff-2026-06-09 §WebSocket protocol —
            // backend broadcasts on every status transition. `staff_name`
            // is present when status === 'active'. The banner + Talk-to-
            // human button visibility flow off this state.
            if (data.type === 'human_handoff_status') {
                const nextStatus = (data.status || 'none') as
                    'none' | 'requested' | 'active' | 'released';
                setHandoffStatus(nextStatus);
                setHandoffStaffName(data.staff_name || null);
                // Hide the AI typing dots the moment a human takes over —
                // the next message bubble is from staff, not the model.
                if (nextStatus === 'active') {
                    setLoading(false);
                    setStatusStage(null);
                }
                return;
            }

            // Phase 8: Shopify storefront action — execute via parent bridge,
            // POST tool-result back to backend. The agent's conversational
            // follow-up arrives on this same WS after the backend re-runs it.
            if (data.type === 'storefront_action' && data.action) {
                handleStorefrontAction(data.action as StorefrontAction, data.payload || {});
                return;
            }

            // Plan-EndConversationUX §B.1 — server broadcasts on
            // `resolve_conversation` / `record_csat` (gated by
            // `turn_complete` so the agent's spoken/text farewell
            // finishes first). HARDENED total teardown: flip to
            // ended state, halt any in-flight loading/listening UI,
            // and explicitly close the WS. The transcript is
            // preserved deliberately — chat history is the user's
            // conversation, not transient session state, and §3.4
            // has the "Start new conversation" CTA on top of it.
            // Voice mode (VoicePlayground sub-component) tears down
            // its own WS/mic/avatar via its own `conversation_ended`
            // handler on the voice WS.
            //
            // IMPORTANT: when `resolve_conversation` fires from VOICE
            // mode, this chat-WS handler does NOT fire. The empirical
            // reason (user-verified 2026-05-15): VoicePlayground.tsx
            // L243 generates a SEPARATE random session id
            // (`session-<random>`) that doesn't match the chat WS's
            // FE-minted `sess_<random>` id. Storm's
            // `_mark_conversation_resolved` →
            // `ConnectionManager.send_personal_message(payload,
            // voice_session_id)` only reaches the voice WS — the
            // chat WS, registered under a different session_id in
            // `active_connections`, never sees the event.
            //
            // For voice-tab parity with the chat-tab CSAT trigger
            // below, VoicePlayground exposes an
            // `onConversationEnded?: () => void` callback that
            // Widget.tsx hooks in the voice-mode return block. The
            // callback fires AFTER VoicePlayground's own teardown
            // chain settles and reuses the same parent state
            // (`csatModalOpen` / `csatSubmittedForConv`) as this
            // chat-WS handler.
            if (data.type === 'conversation_ended') {
                // Task #17 — per-step trace. Widget.tsx owns only the
                // chat WS; voice WS (and avatar/mic teardown) lives in
                // VoicePlayground's parallel conversation_ended handler.
                // These logs let us correlate the chat-side teardown
                // against the voice-side teardown chain.
                console.log('[Widget.conversation_ended] enter', {
                    chatWsState: ws.current?.readyState,
                    sessionEnded,
                    loading,
                    isListening,
                });
                setSessionEnded(true);
                console.log('[Widget.conversation_ended] sessionEnded set true');
                setLoading(false);
                setStatusStage(null);
                setIsListening(false);
                console.log('[Widget.conversation_ended] loading + listening cleared');
                try {
                    // 🚨 PROTECTED auto-disconnect. Record the intent BEFORE
                    // closing — `handleClose` fires synchronously off this
                    // call, and if the flag were set after, the close would be
                    // read as an abnormal drop and reconnect would resurrect
                    // the conversation the agent just ended.
                    intentionalCloseRef.current = true;
                    ws.current?.close();
                    console.log('[Widget.conversation_ended] chat ws closed');
                } catch (err) {
                    console.warn('[Widget.conversation_ended] chat ws close threw', err);
                }
                // Plan-CSATUIModal.md §B.2 (2026-05-15) — open the CSAT
                // modal AFTER the chat-WS teardown lines above. The
                // protected auto-disconnect chain (WS close + loading
                // clear + listening clear + setSessionEnded) runs
                // first; the modal is purely UI overlay AFTER teardown
                // completes. Gated by `config.ask_for_csat` (default
                // true on backend) AND a per-conversation guard so a
                // double-fired event doesn't re-pop after the user
                // submitted. The voice-tab path receives the same
                // `conversation_ended` broadcast (Storm Phase A — chat
                // and voice WS are joined to the same session room),
                // so this single trigger covers both surfaces.
                if (
                    config?.ask_for_csat !== false &&
                    sessionId &&
                    sessionId !== csatSubmittedForConv
                ) {
                    // Chat-mode resolve_conversation: the conversation
                    // doc lives under the chat WS sessionId, so that's
                    // what the CSAT POST needs. Voice mode sets this
                    // to the voice session_id via the
                    // `onConversationEnded` callback below.
                    setCsatConversationId(sessionId);
                    setCsatModalOpen(true);
                }
                console.log('[Widget.conversation_ended] exit complete');
                return;
            }

            // Plan-HumanAgentHandoff-2026-06-12 — Storm's
            // `broadcast_to_session` (back/api/widget.py:182) wraps
            // every new message in an envelope:
            //   {type:"new_message", message:{role, content, timestamp,
            //    attachments, from_human?, author_name?}, from_human}
            // and fans out to BOTH the widget WS (when `include_widget=
            // True`, the default — used for staff replies on
            // chats.py:285) AND the staff subscribers. The pre-handoff
            // widget WS path only knew the bare `{role, content, ...}`
            // shape, so the envelope dropped to the fallback below and
            // appended a content-less object — which then exploded in
            // the render filter (`stripStorefrontTokens(undefined)`).
            // Unwrap the envelope explicitly so it lands in the same
            // bubble-render path as a direct agent frame.
            if (data.type === 'new_message' && data && typeof data.message === 'object' && data.message) {
                const inner = data.message as any;
                if (typeof inner.content !== 'string' && inner.kind !== 'video') {
                    console.warn('[widget] WS new_message missing string content, skipping', data);
                    return;
                }
                const innerIsVideo = inner.kind === 'video' && inner.video && inner.video.url;
                const innerFromHuman = inner.role === 'agent'
                    && (inner.from_human === true || data.from_human === true);
                wsDeliveredRef.current = true;
                setLoading(false);
                setStatusStage(null);
                setMessages((prev) => [...prev, {
                    role: inner.role,
                    content: inner.content || '',
                    timestamp: inner.timestamp || new Date().toISOString(),
                    attachments: inner.attachments,
                    kind: innerIsVideo ? 'video' : undefined,
                    video: innerIsVideo
                        ? { url: inner.video.url, label: inner.video.label || '', thumbnail: inner.video.thumbnail || undefined }
                        : undefined,
                    from_human: innerFromHuman || undefined,
                    // Storm names the staff display field `author_name`
                    // on the staff reply envelope (chats.py:280); honor
                    // either name so a future rename doesn't break
                    // labeling.
                    ...((inner.staff_name || inner.author_name)
                        ? { staff_name: (inner.staff_name || inner.author_name) as string }
                        : {}),
                } as Message]);
                if (inner.role === 'agent' && !innerIsVideo && !isAutoReadEnabled) {
                    playNotificationChime();
                }
                return;
            }

            // Plan-WidgetStreaming Phase 3 — progressive token streaming.
            // Emitted only when the backend flag WIDGET_STREAMING_ENABLED is
            // on; the delta field is `text`, NOT `content`. The authoritative
            // final `{role:"agent", content, message_id}` frame still arrives
            // after `stream_end` and reconciles below (it REPLACES this
            // placeholder in place). Flag off ⇒ none of these frames arrive and
            // behaviour is byte-for-byte unchanged.
            if (data.type === 'stream_start' && data.message_id != null) {
                const mid = String(data.message_id);
                // The stream is the delivery — suppress the HTTP-response
                // fallback the same way a normal WS reply does.
                wsDeliveredRef.current = true;
                setLoading(false);
                setStatusStage(null);
                setMessages((prev) => {
                    // Defensive against a duplicated start frame: never open two
                    // placeholders for one message_id.
                    if (prev.some((m) => m.message_id === mid && m.streaming)) return prev;
                    return [...prev, {
                        role: 'agent',
                        content: '',
                        timestamp: new Date().toISOString(),
                        message_id: mid,
                        streaming: true,
                    } as Message];
                });
                return;
            }

            if (data.type === 'stream_delta' && data.message_id != null) {
                const mid = String(data.message_id);
                const delta = typeof data.text === 'string' ? data.text : '';
                if (!delta) return;
                setMessages((prev) => {
                    let matched = false;
                    const next = prev.map((m) => {
                        if (m.message_id === mid && m.streaming) {
                            matched = true;
                            return { ...m, content: (m.content || '') + delta };
                        }
                        return m;
                    });
                    // A delta before its start (out-of-order / missed start):
                    // open the placeholder now so no text is lost.
                    if (!matched) {
                        return [...prev, {
                            role: 'agent',
                            content: delta,
                            timestamp: new Date().toISOString(),
                            message_id: mid,
                            streaming: true,
                        } as Message];
                    }
                    return next;
                });
                return;
            }

            if (data.type === 'stream_end' && data.message_id != null) {
                const mid = String(data.message_id);
                setMessages((prev) =>
                    prev.map((m) =>
                        m.message_id === mid && m.streaming
                            ? { ...m, streaming: false }
                            : m,
                    ),
                );
                return;
            }

            // Real agent/AI message streamed immediately.
            // Option C: structured video bubbles arrive on this same channel
            // as `{role:"agent", kind:"video", content:"", video:{...}}` —
            // discriminator is `kind === "video"`, no separate event type.
            if (data.role === 'agent' || data.role === 'user') {
                wsDeliveredRef.current = true;
                setLoading(false);
                setStatusStage(null);
                const isVideo = data.kind === 'video' && data.video && data.video.url;
                // Forward-compat, INERT today: `api/widget.py`'s failure frame
                // ("I ran into an issue processing your request.") is an
                // untagged {role, content, timestamp}, indistinguishable from a
                // normal reply. If the backend ever stamps `kind: "error"` on
                // it, the same recovery actions light up with no FE change.
                const isError = data.kind === 'error';
                // Plan-HumanAgentHandoff §Customer UI — staff replies
                // are stamped server-side with `from_human: true` on the
                // canonical message doc (Storm task #32), so the FE
                // takes that signal as authoritative and does NOT infer
                // from `handoffStatus === 'active'`. Inferring would
                // mis-label a stray AI reply mid-handoff, AND wouldn't
                // help on resume (a RELEASED conversation's handoff
                // status is no longer 'active' but its past staff
                // messages still need the staff visual treatment).
                const fromHuman = data.role === 'agent' && data.from_human === true;
                const finalMsg = {
                    role: data.role,
                    content: data.content || '',
                    timestamp: data.timestamp || new Date().toISOString(),
                    attachments: data.attachments,
                    kind: isVideo ? 'video' : isError ? 'error' : undefined,
                    video: isVideo
                        ? { url: data.video.url, label: data.video.label || '', thumbnail: data.video.thumbnail || undefined }
                        : undefined,
                    from_human: fromHuman || undefined,
                    // Carry the streamed id through so the reconcile below can
                    // find the placeholder this frame supersedes.
                    ...(data.message_id != null ? { message_id: String(data.message_id) } : {}),
                    // Per-message staff name snapshot, if backend sends
                    // one with the frame. Lets the bubble label fall
                    // back from conversation-level `handoffStaffName`
                    // to a fresh value even on a released conversation.
                    ...(data.staff_name ? { staff_name: data.staff_name as string } : {}),
                } as Message;
                // Plan-WidgetStreaming Phase 3 — if this authoritative frame
                // carries a message_id that matches a streamed placeholder,
                // REPLACE it in place (authoritative text wins) instead of
                // appending a duplicate bubble. No match (flag off, or a
                // non-streamed reply) ⇒ append exactly as before.
                const streamedId = data.message_id != null ? String(data.message_id) : null;
                setMessages((prev) => {
                    if (streamedId && prev.some((m) => m.message_id === streamedId)) {
                        return prev.map((m) =>
                            m.message_id === streamedId ? { ...finalMsg, streaming: false } : m,
                        );
                    }
                    return [...prev, finalMsg];
                });
                if (data.role === 'agent' && !isVideo && !isAutoReadEnabled) {
                    playNotificationChime();
                }
                return;
            }

            // 🚨 UNKNOWN CONTROL FRAMES ARE IGNORED, NOT RENDERED.
            //
            // Reaching here with a `type` means the backend sent a control
            // frame this build does not know about. Control frames are never
            // bubbles — every real message frame is identified by `role`
            // (`{role, content, timestamp}`), never by `type`. Falling through
            // to the append below would paint an empty bubble into a customer's
            // conversation, and a content-less one previously exploded in the
            // render filter (`stripStorefrontTokens(undefined)`).
            //
            // This is the frontend half of the keepalive contract: it makes the
            // FE forward-compatible with ANY future control frame, so the
            // backend can start emitting one without a lockstep FE deploy.
            // Without it, an emitter shipped first paints blank bubbles.
            if (typeof data?.type === 'string') {
                console.warn('[widget] WS control frame ignored — unknown type', data.type);
                return;
            }

            // Fallback: pass unknown messages through as before, BUT
            // only if they look message-shaped (have a string content).
            // Otherwise a stray envelope with no content lands in the
            // transcript and crashes the render filter below.
            if (typeof data?.content !== 'string') {
                console.warn('[widget] WS frame ignored — no string content', data);
                return;
            }
            setMessages((prev) => [...prev, data]);
        };

        // Plan-WidgetReconnect §3 — keeping the socket warm treats the CAUSE
        // of the 1006, and it is deliberately a SERVER-side job, not ours.
        //
        // 🚨 NO CLIENT HEARTBEAT IS SENT, ON PURPOSE. The widget WS endpoint is
        // send-only — Storm AST-verified ZERO `receive_*` calls in
        // `back/api/widget.py`'s socket handler. Nothing ever drains what a
        // client sends, so client frames would queue in the ASGI buffer for the
        // life of the connection with no reader: a slow per-connection leak
        // bought in exchange for nothing the server can act on. (Browsers also
        // expose no protocol-level ping, so a real WS PING is not an option.)
        //
        // The fix is Storm emitting a server→client keepalive on the send
        // loop's existing 30s `asyncio.wait_for(queue.get(), timeout=30)` wake,
        // which currently sends nothing — which is exactly why an idle
        // connection carries no traffic and the ALB reaps it. Our half of that
        // contract is tolerating the frame; see the `keepalive` early-return in
        // `handleMessage`.

        /**
         * Plan-WidgetReconnect §2 — recover the replies that were pushed into
         * the dead socket. THIS is what actually fixes the reported bug;
         * reconnect alone leaves the customer connected and still missing the
         * answer, which would LOOK fixed without being fixed.
         */
        const replayMissedMessages = async () => {
            // Same fallback the load_session path uses — the ref can still be
            // empty if a drop lands before the visitorId effect has synced, and
            // the endpoint 400s without one.
            const vid = visitorIdRef.current
                || localStorage.getItem('prodagi_ai_visitor_id')
                || '';
            if (!sessionId || !vid) return;
            try {
                const resp = await widgetAPI.getConversation(
                    sessionId, vid, channelInfoRef.current.is_playground === true,
                );
                const server = (resp.data.messages || []) as any[];
                if (!server.length) return;

                // Normalize the server rows exactly the way the resume path
                // does, so identities are comparable: same token stripping,
                // same "drop empty" rule.
                const cleaned = server
                    .map((m) => ({
                        role: m.role as 'user' | 'agent',
                        content: stripStorefrontTokens(m.content || ''),
                        timestamp: m.timestamp,
                        attachments: m.attachments,
                    }))
                    .filter((m) => m.content || (m.attachments && m.attachments.length));

                setMessages((prev) => {
                    // 🚨 Drop any bubble still mid-stream. A socket that died
                    // DURING a stream leaves a placeholder holding PARTIAL
                    // delta text; that partial text cannot match the server's
                    // complete message, so keeping it would both strand a
                    // truncated bubble on screen AND let the full message
                    // append beside it — a double render. The authoritative
                    // copy is in the replay, so discarding the fragment loses
                    // nothing. (Plan-WidgetStreaming: delta key is `text`.)
                    const settled = prev.filter((m) => !m.streaming);
                    const additions = computeReplayAdditions(
                        settled.map((m) => ({ role: m.role, content: m.content || '' })),
                        cleaned.map((m) => ({ role: m.role, content: m.content })),
                    );
                    if (!additions.length) return settled.length === prev.length ? prev : settled;
                    // Re-attach the full rows for the identities we decided are new.
                    const pool = [...cleaned];
                    const appended: Message[] = [];
                    for (const a of additions) {
                        const idx = pool.findIndex(
                            (c) => c.role === a.role && (c.content || '').trim() === a.content.trim(),
                        );
                        if (idx >= 0) {
                            const [row] = pool.splice(idx, 1);
                            appended.push(row as Message);
                        }
                    }
                    return appended.length ? [...settled, ...appended] : settled;
                });

                // A conversation resolved while we were disconnected must land
                // in ended-state, not silently accept another message.
                if ((resp.data as { is_ended?: boolean }).is_ended === true) {
                    setSessionEnded(true);
                }
            } catch (err) {
                // Replay is best-effort — a failed fetch must never take the
                // widget down. The socket is live either way.
                console.warn('[Prodagi] reconnect replay failed:', err);
            }
        };

        const scheduleReconnect = () => {
            if (reconnectAttemptsRef.current >= WS_RECONNECT_MAX_ATTEMPTS) {
                setConnState('lost');
                return;
            }
            const delay = wsReconnectDelay(reconnectAttemptsRef.current);
            reconnectAttemptsRef.current += 1;
            setConnState('retrying');
            reconnectTimerRef.current = setTimeout(() => {
                // Re-check intent at FIRE time, not just at schedule time — the
                // conversation can resolve, or the widget can be torn down,
                // during the backoff window.
                if (intentionalCloseRef.current || sessionEndedRef.current) return;
                connect();
            }, delay);
        };

        const handleClose = (event: CloseEvent) => {
            console.log('WebSocket disconnected', event?.code, event?.reason);
            // Plan-EffectiveLimitsAndMaintenanceBanners.md §2b — backend
            // closes the chat WS with code 1008 reason
            // `chat_limit_exhausted` when the workspace runs out of chat
            // budget mid-conversation (and auto-overage for chat is
            // off). Flip the local maintenance flag immediately so the
            // chat tab swaps to the maintenance card on the next render
            // — no config refetch needed. Plan-TextVoicePoolBilling §A4
            // adds `text_limit_exhausted` as the canonical reason on the
            // pooled Text allowance; `isChatExhaustedReason` accepts both.
            if (event && event.code === 1008 && isChatExhaustedReason(event.reason)) {
                setChatLimitExhausted(true);
                setConnState('live');
                return; // budget is out — a retry would just be refused again.
            }

            // ── 🚨 TWO INDEPENDENT GATES, BOTH MUST PASS ──────────────────
            //
            // GATE 1 — FE-recorded intent. WE closed it: the
            // `conversation_ended` teardown, the `prodagi_widget_close`
            // launcher bridge, or effect cleanup. Reconnecting after any of
            // those would resurrect a conversation we deliberately ended — the
            // PROTECTED auto-disconnect regression. This gate exists because
            // the resolve teardown is CLIENT-initiated (the server broadcasts
            // a `conversation_ended` frame and never closes the socket
            // itself), so there is no server close code to key on at all.
            if (intentionalCloseRef.current || sessionEndedRef.current) {
                setConnState('live');
                return;
            }

            // GATE 2 — 🚨 ALLOWLIST, NEVER A DENYLIST. Reconnect on 1006 and
            // nothing else.
            //
            // 1006 is "abnormal closure, no close frame received" — the
            // ALB/CloudFront idle timeout and genuine network loss. Every
            // other code is either a deliberate close or a refusal a retry
            // cannot fix:
            //   1000 `conversation_resolved`  — resume-of-resolved gate
            //   1008 chat/text_limit_exhausted — allowance out (handled above)
            //   4002 `trial_expired`          — workspace gate
            //   1005 (no status received)     — see below
            //
            // 🚨 THE WORKED EXAMPLE FOR WHY THIS IS AN ALLOWLIST: a bare
            // `ws.close()` — which our own effect cleanup calls on EVERY
            // unmount and EVERY `sessionEpoch` bump — surfaces as **1005**,
            // not 1006. 1005 is not an "error code" anyone would think to add
            // to a denylist, so a denylist (`code !== 1000 && code !== 1008
            // && …`) would reconnect on it and start fighting our own
            // component teardown, racing a second socket against the one the
            // effect is about to open. An allowlist ignores 1005 by
            // construction. It also means a NEW backend gate code added later
            // is ignored by default instead of silently becoming reconnectable.
            if (event?.code !== 1006) {
                setConnState('live');
                return;
            }

            // Abnormal drop. The agent may have answered into this dead
            // socket, so a replay is owed on whichever attempt succeeds —
            // recorded even though replay is currently HELD (see
            // REPLAY_ENABLED), so wiring it later needs no new bookkeeping.
            replayOwedRef.current = true;
            scheduleReconnect();
        };

        function connect() {
            // 🚨 SINGLE-SOCKET INVARIANT. `sessionEpoch` is bumped elsewhere in
            // this file (launcher reopen, `handleNewChat`, auto-send) precisely
            // to force a fresh socket, and this effect's cleanup closes the old
            // one. A retry loop running beside that mechanism is how you end up
            // with two live sockets and every message rendered twice.
            //
            // They cannot both be live, by construction:
            //   • cleanup sets `intentionalCloseRef` AND clears the pending
            //     retry timer, so an in-flight backoff cannot outlive the
            //     effect that scheduled it;
            //   • the retry callback re-checks intent at FIRE time;
            //   • the bare `ws.close()` in cleanup surfaces as 1005, which the
            //     allowlist ignores;
            //   • and this guard refuses to open a second socket while one is
            //     already OPEN or CONNECTING.
            const existing = ws.current;
            if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
                return;
            }
            // Each socket must earn its own ack.
            connAckedRef.current = false;
            try {
                ws.current = new WebSocket(
                    `${wsUrl}/api/widget/ws/${sessionId}${channelInfoRef.current.is_playground ? '?source=playground' : ''}`,
                );
            } catch (err) {
                console.warn('[Prodagi] WS construction failed:', err);
                scheduleReconnect();
                return;
            }

            ws.current.onopen = () => {
                console.log('WebSocket connected');
                // 🚨 The retry-budget reset, `connState: 'live'` and the
                // (held) replay moved to the FIRST-FRAME branch in
                // `handleMessage` — an accepted socket is not yet a working
                // one. See `connAckedRef`.
            };
            ws.current.onmessage = handleMessage;
            ws.current.onclose = handleClose;
            ws.current.onerror = () => {
                // `onerror` is always followed by `onclose`; let that single
                // path own the retry decision so we never double-schedule.
                console.warn('[Prodagi] WS error');
            };
        }

        connect();

        // Manual retry after the backoff budget is spent. Resets the budget and
        // reconnects immediately; `replayOwedRef` is still set from the drop, so
        // a success here recovers the missed reply too.
        manualRetryRef.current = () => {
            if (intentionalCloseRef.current || sessionEndedRef.current) return;
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
            reconnectAttemptsRef.current = 0;
            replayOwedRef.current = true;
            setConnState('retrying');
            connect();
        };

        return () => {
            // Unmount / session change is a deliberate close.
            intentionalCloseRef.current = true;
            manualRetryRef.current = null;
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
            ws.current?.close();
        };
    }, [sessionId, sessionEpoch]);

    // Launcher → iframe bridge (Fix B from team-lead). The parent
    // widget.js posts `prodagi_widget_close` when the user clicks the
    // launcher to hide the panel; we tear down the chat WS + TTS +
    // listening + pending attachments + loading spinner so the iframe
    // is in a clean "idle" state. `prodagi_widget_open` bumps the
    // sessionEpoch which retriggers the WS useEffect above for a fresh
    // connection.
    //
    // We INTENTIONALLY preserve `messages` across close/open — chat
    // history is the user's conversation, not transient session state.
    // Voice mode tears down its own WS via VoicePlayground's
    // sibling message listener (it owns `liveAvatar.disconnect()`
    // and the mic stream).
    //
    // `event.source === window.parent` is the safe origin check —
    // widget.js posts with targetOrigin '*' so we can't pin a string,
    // but the parent reference is forgery-resistant.
    useEffect(() => {
        const handler = (event: MessageEvent) => {
            if (event.source !== window.parent) return;
            const data = event.data;
            if (!data || typeof data !== 'object') return;
            if (data.type === 'prodagi_widget_close') {
                // Launcher collapse is deliberate — do not reconnect behind a
                // closed panel. `prodagi_widget_open` bumps sessionEpoch, which
                // re-runs the WS effect and clears this flag for the new socket.
                intentionalCloseRef.current = true;
                try { ws.current?.close(); } catch { /* ignore */ }
                if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
                    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
                }
                setPendingAttachments((prev) => {
                    prev.forEach((pa) => { if (pa.previewUrl) URL.revokeObjectURL(pa.previewUrl); });
                    return [];
                });
                setLoading(false);
                setIsListening(false);
                setIsAutoReadEnabled(false);
                // messages preserved deliberately.
            } else if (data.type === 'prodagi_widget_open') {
                setSessionEpoch((prev) => prev + 1);
            } else if (data.type === 'prodagi_widget_identify') {
                // Identity passthrough. Standalone (a top-level tab, where
                // `window.parent === window` also passes the source gate) has
                // no host page to trust — no-op there.
                if (window.parent === window) return;
                const next = typeof data.token === 'string' && data.token.trim()
                    ? data.token.trim()
                    : null;
                // widget.js delivers on iframe `load` AND on our ready
                // handshake, so the same token routinely arrives twice.
                if (next === identityTokenRef.current) return;
                identityTokenRef.current = next;
                // Host logout — drop the identity from every chat session this
                // iframe verified, not just the current one.
                if (next === null) chatIdentitySyncRef.current?.clearAll();
                setIdentityRevision((prev) => prev + 1);
            } else if (data.type === 'prodagi_widget_send_message') {
                // Plan-EnterpriseUpsellViaWidget §A.1 — parent page (e.g.
                // BillingPage / Pricing "Upgrade to Enterprise" CTA)
                // postMessages an auto-injected user message. We bump
                // sessionEpoch first so a clean WS connection is in
                // flight, then hand off to handleSend with the override
                // text. Guard against missing/empty text — never send
                // an empty user message.
                if (typeof data.text === 'string' && data.text.trim()) {
                    if (data.open) {
                        setSessionEpoch((prev) => prev + 1);
                    }
                    // Defer one tick so the WS effect re-runs after the
                    // epoch bump; handleSend hits the live socket.
                    const text = data.text;
                    setTimeout(() => {
                        try {
                            handleSendRef.current?.(text);
                        } catch (err) {
                            console.warn('[Prodagi] auto-send failed:', err);
                        }
                    }, 0);
                }
            } else if (data.type === 'load_session' && typeof data.sessionId === 'string') {
                // Plan-PlaygroundIframeWidget.md §F.3 — Playground page asks
                // the iframe-embedded widget to switch to a specific
                // historical session. Setting `sessionId` retriggers the chat
                // WS useEffect (which depends on [sessionId, sessionEpoch])
                // and the existing resume-from-localStorage logic — but here
                // we bypass localStorage and load directly via the widget's
                // existing getConversation path on mount.
                setSessionId(data.sessionId);
                // Bump epoch too so the WS effect refires even when the
                // sessionId happens to match the current one.
                setSessionEpoch((prev) => prev + 1);
                // Best-effort conversation load — mirrors the resume path
                // logic at L350 but unconditionally fires for the loaded id.
                const vid = visitorIdRef.current || localStorage.getItem('prodagi_ai_visitor_id') || '';
                if (vid) {
                    widgetAPI.getConversation(data.sessionId, vid, channelInfoRef.current.is_playground === true)
                        .then((resp) => {
                            const cleaned: Message[] = [];
                            for (const m of (resp.data.messages || []) as any[]) {
                                if (m.kind === 'video' && m.video && m.video.url) {
                                    cleaned.push({
                                        role: 'agent',
                                        content: '',
                                        timestamp: m.timestamp,
                                        kind: 'video',
                                        video: { url: m.video.url, label: m.video.label || '', thumbnail: m.video.thumbnail },
                                    });
                                    continue;
                                }
                                const stripped = stripStorefrontTokens(m.content || '');
                                const atts = m.attachments;
                                if (!stripped && !(atts && atts.length)) continue;
                                cleaned.push({
                                    role: m.role as 'user' | 'agent',
                                    content: stripped,
                                    timestamp: m.timestamp,
                                    attachments: atts,
                                });
                            }
                            setMessages(cleaned);
                            // Plan-EndConversationUX §B.4 — same
                            // is_ended hydration on Playground's
                            // load_session path. Without this, picking
                            // a resolved session from the history list
                            // would render an interactive chat against
                            // an ended session.
                            if ((resp.data as { is_ended?: boolean }).is_ended === true) {
                                setSessionEnded(true);
                            } else {
                                setSessionEnded(false);
                            }
                        })
                        .catch((err) => {
                            console.warn('[Prodagi] load_session failed:', err);
                        });
                }
            } else if (data.type === 'reset_visitor') {
                // Plan-PlaygroundIframeWidget.md §F.3 — Playground "Reset
                // Visitor" button simulates a brand-new customer. Drop the
                // persistent visitor_id + any persisted session, then full
                // page reload so the widget remints visitor/session from
                // scratch. Localstorage scoped to the iframe origin only.
                try { localStorage.removeItem('prodagi_ai_visitor_id'); } catch { /* ignore */ }
                try { localStorage.removeItem('prodagi_widget_session'); } catch { /* ignore */ }
                window.location.reload();
            }
        };
        window.addEventListener('message', handler);
        // Identity passthrough handshake — tell widget.js the listener is now
        // attached. The iframe `load` event can fire before this effect runs,
        // so a token posted only on `load` could be lost. Carries nothing
        // sensitive (the reply is origin-pinned by widget.js), hence '*'. A
        // parent that is not widget.js simply ignores the unknown type.
        if (window.parent !== window) {
            try {
                window.parent.postMessage({ type: 'prodagi_widget_identity_ready' }, '*');
            } catch { /* fail silent */ }
        }
        return () => window.removeEventListener('message', handler);
    }, []);

    // Identity passthrough — verify the held token against the CURRENT chat
    // session whenever the token or the session changes (new conversation,
    // resume, load_session, reopen epoch). Clears on `identify(null)` happen in
    // the message handler (`clearAll`), so this effect only ever verifies.
    //
    // Race safety: nothing from the response is applied to UI state, so a slow
    // reply for an old session id cannot leak onto the new one; ordering on the
    // wire (verify vs clear) is guaranteed by the serialized IdentitySync queue.
    // The OTP flow is untouched — a verified session just stops being gated.
    useEffect(() => {
        const token = identityTokenRef.current;
        if (!token || !sessionId || !agentId) return;
        void chatIdentitySyncRef.current?.push(sessionId, agentId, token);
    }, [sessionId, sessionEpoch, identityRevision, agentId]);

    // Phase 8: parent-window bridge — listen for prodagi-storefront-result
    // replies from the Shopify theme loader. Correlate by request_id and
    // resolve the matching pending action so its Promise completes.
    useEffect(() => {
        const handleBridgeReply = (e: MessageEvent) => {
            const data = e.data;
            if (!data || typeof data !== 'object') return;
            if (data.type !== 'prodagi-storefront-result') return;
            const resolver = pendingActionsRef.current.get(data.request_id);
            if (resolver) {
                pendingActionsRef.current.delete(data.request_id);
                resolver(data);
            }
        };
        window.addEventListener('message', handleBridgeReply);
        return () => window.removeEventListener('message', handleBridgeReply);
    }, []);

    // Execute a storefront action by asking the parent window (Shopify theme
    // loader) to run it same-origin, then return the parent's reply.
    const executeViaParent = (action: StorefrontAction, payload: any): Promise<any> => {
        return new Promise((resolve) => {
            if (!window.parent || window.parent === window) {
                resolve({ status: 'error', error_code: 'no_parent', error_message: 'Widget not embedded in a parent frame' });
                return;
            }
            const requestId =
                (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)) + '_' + Date.now();
            pendingActionsRef.current.set(requestId, resolve);
            // Safety: don't let an orphaned request hang forever
            setTimeout(() => {
                if (pendingActionsRef.current.has(requestId)) {
                    pendingActionsRef.current.delete(requestId);
                    resolve({ status: 'error', error_code: 'parent_timeout', error_message: 'No response from storefront bridge' });
                }
            }, 15000);
            window.parent.postMessage(
                { type: 'prodagi-storefront-action', request_id: requestId, action, payload },
                '*'
            );
        });
    };

    const handleStorefrontAction = async (action: StorefrontAction, payload: any) => {
        // Navigating actions wipe the current page, so persist session state
        // *before* we ask the parent to execute them. Also post a best-effort
        // tool-result beforehand since the POST may not complete before the
        // navigation happens.
        if (NAVIGATING_ACTIONS.has(action) && agentId && visitorId && sessionId) {
            persistSession({
                session_id: sessionId,
                visitor_id: visitorId,
                agent_id: agentId,
                channel: channelInfoRef.current.channel,
                shop_domain: channelInfoRef.current.shop_domain,
                customer_id: channelInfoRef.current.customer_id ?? null,
                ts: Date.now(),
            });
        }

        const reply = await executeViaParent(action, payload);
        try {
            await widgetAPI.toolResult({
                session_id: sessionId,
                visitor_id: visitorId,
                tool_name: action,
                status: reply.status === 'success' ? 'success' : 'error',
                result: reply.result ?? null,
                error_code: reply.error_code ?? null,
                error_message: reply.error_message ?? null,
            });
        } catch (err) {
            // Backend unreachable — log and move on; the agent will time out its turn.
            console.error('[Prodagi] tool-result POST failed:', err);
        }
    };

    // Plan-VoiceCartRoundTrip — voice variant of handleStorefrontAction.
    // Runs the SAME parent bridge (executeViaParent) so the storefront
    // execution is identical to chat, but DOES NOT POST the result over
    // HTTP. Instead it returns the normalized outcome to VoicePlayground,
    // which sends it back over the VOICE WS as a `tool_result` frame
    // correlated by function_call_id. Kept separate from the chat handler
    // so the chat add-to-cart path is untouched (PROTECTED #1).
    const runVoiceStorefrontAction = async (
        actionRaw: string,
        payload: any,
    ): Promise<{
        status: 'success' | 'error';
        result: any;
        error_code: string | null;
        error_message: string | null;
    }> => {
        const action = actionRaw as StorefrontAction;
        // Mirror the chat handler's pre-navigation session persistence so a
        // checkout/navigate that wipes the page can still be resumed.
        if (NAVIGATING_ACTIONS.has(action) && agentId && visitorId && sessionId) {
            persistSession({
                session_id: sessionId,
                visitor_id: visitorId,
                agent_id: agentId,
                channel: channelInfoRef.current.channel,
                shop_domain: channelInfoRef.current.shop_domain,
                customer_id: channelInfoRef.current.customer_id ?? null,
                ts: Date.now(),
            });
        }

        const reply = await executeViaParent(action, payload);
        return {
            status: reply.status === 'success' ? 'success' : 'error',
            result: reply.result ?? null,
            error_code: reply.error_code ?? null,
            error_message: reply.error_message ?? null,
        };
    };

    const loadConfig = async (id: string) => {
        try {
            const response = await widgetAPI.getConfig(id);
            setConfig(response.data);
            // Add welcome message if chat is empty
            if (messages.length === 0 && response.data.welcome_message) {
                setMessages([
                    {
                        role: 'agent',
                        content: response.data.welcome_message,
                        timestamp: new Date().toISOString(),
                    },
                ]);
            }
        } catch (error) {
            console.error('Failed to load widget config:', error);
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, loading]);

    // Returning from a HELP_CENTER article view (articleViewSlug → null)
    // REMOUNTS the message list at scrollTop=0, but the [messages, loading]
    // scroll effect above won't re-fire (messages didn't change) — leaving the
    // customer stranded at the TOP of a long chat. Snap back to the bottom
    // once the remounted list has laid out and messagesEndRef is reattached.
    // Instant (behavior:'auto') — smooth-scrolling a long list from the top is
    // janky. Only in chat mode.
    useEffect(() => {
        if (mode !== 'chat' || articleViewSlug !== null) return;
        const raf = requestAnimationFrame(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
        });
        return () => cancelAnimationFrame(raf);
    }, [articleViewSlug, mode]);

    // Pick the best available TTS voice (natural-sounding preferred)
    useEffect(() => {
        if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
        const pickVoice = () => {
            const voices = speechSynthesis.getVoices();
            if (!voices.length) return;
            const preferred = [
                'Google UK English Female',
                'Google US English',
                'Microsoft Jenny Online (Natural)',
                'Microsoft Aria Online (Natural)',
                'Samantha',
            ];
            for (const name of preferred) {
                const match = voices.find(v => v.name.includes(name));
                if (match) { preferredVoiceRef.current = match; return; }
            }
            const cloudEnglish = voices.find(v => v.lang.startsWith('en') && !v.localService);
            if (cloudEnglish) { preferredVoiceRef.current = cloudEnglish; return; }
            const anyEnglish = voices.find(v => v.lang.startsWith('en'));
            if (anyEnglish) preferredVoiceRef.current = anyEnglish;
        };
        pickVoice();
        speechSynthesis.addEventListener('voiceschanged', pickVoice);
        return () => speechSynthesis.removeEventListener('voiceschanged', pickVoice);
    }, []);

    // Text-to-Speech for agent messages
    useEffect(() => {
        const lastMessage = messages[messages.length - 1];
        // Suppress browser TTS while voice mode is active — Gemini Live
        // already streams native audio, so reading the same agent reply
        // through speechSynthesis would double-narrate. Embed-locked voice
        // surface counts too.
        const isVoiceSurface =
            mode === 'voice' || embedSurfaceRef.current === 'voice';
        if (
            lastMessage &&
            lastMessage.role === 'agent' &&
            !loading &&
            isAutoReadEnabled &&
            !isVoiceSurface
        ) {
            if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
                // LOOK markers carry nested arrays/objects, so the flat
                // `\{[^[\]]*\}` regex below cannot match them — strip via the
                // brace-balanced helper FIRST so the voice path never speaks
                // raw `[LOOK:...]`.
                const cleanText = stripLookMarkers(stripStorefrontTokens(lastMessage.content))
                    .replace(/\[(PRODUCT_CARD|CHECKOUT_LINK|HELP_CENTER_CARD|HELP_CENTER_PAGE|HELP_CENTER_MEDIA|ORDER_CARD):\{[^[\]]*\}\]/g, '')
                    .replace(/[*#_`~]/g, '')
                    .replace(/\[(.*?)\]\(.*?\)/g, '$1');
                if (!cleanText.trim()) return;
                window.speechSynthesis.cancel();
                const utterance = new SpeechSynthesisUtterance(cleanText);
                utterance.lang = 'en-US';
                utterance.rate = 1.0;
                utterance.pitch = 1.05;
                utterance.volume = 1.0;
                if (preferredVoiceRef.current) utterance.voice = preferredVoiceRef.current;
                window.speechSynthesis.speak(utterance);
            }
        }
    }, [messages, loading, isAutoReadEnabled, mode]);

    const handleVoiceInput = () => {
        if (isListening) {
            setIsListening(false);
            return;
        }

        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SpeechRecognition) {
            alert('Speech recognition is not supported in this browser.');
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = 'en-US';
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        recognition.onstart = () => {
            setIsListening(true);
            setIsAutoReadEnabled(true); // Enable auto-read when voice is used
        };

        recognition.onresult = (event: any) => {
            const transcript = event.results[0][0].transcript;
            setInput(transcript);
            // Auto-send
            setTimeout(() => {
                handleSend(transcript);
            }, 500);
        };

        recognition.onerror = (event: any) => {
            console.error('Speech recognition error', event.error);
            setIsListening(false);
        };

        recognition.onend = () => {
            setIsListening(false);
        };

        recognition.start();
    };

    // Paste handler for images
    useEffect(() => {
        const handlePaste = (e: ClipboardEvent) => {
            if (!e.clipboardData?.files?.length) return;
            const imageFiles = Array.from(e.clipboardData.files).filter(f => f.type.startsWith('image/'));
            if (imageFiles.length === 0) return;
            e.preventDefault();
            addFiles(imageFiles);
        };
        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, []);

    const addFiles = (files: File[]) => {
        const newEntries: PendingAttachment[] = files.map(file => ({
            file,
            previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
            type: file.type.startsWith('image/') ? 'image' : 'document',
        }));
        setPendingAttachments(prev => [...prev, ...newEntries]);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length) return;
        addFiles(Array.from(e.target.files));
        e.target.value = '';
    };

    const removePendingAttachment = (index: number) => {
        setPendingAttachments(prev => {
            const entry = prev[index];
            if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
            return prev.filter((_, i) => i !== index);
        });
    };

    // ---- Virtual Try-On (Phase 3) orchestration ---------------------------
    // Poll-based: seed a loading card → ensure a person photo → POST /tryon →
    // poll /tryon/{job_id} every 2s → progressive render. Purely additive to
    // the chat flow; none of this runs unless a try-on button (gated on VTO
    // enabled, chat-only) is tapped.
    const updateTryOnJob = (localId: string, patch: Partial<TryOnJob>) => {
        setTryOnJobs(prev => prev.map(j => (j.localId === localId ? { ...j, ...patch } : j)));
    };

    const stopTryOnPoll = (localId: string) => {
        const t = tryOnPollsRef.current[localId];
        if (t) { clearInterval(t); delete tryOnPollsRef.current[localId]; }
    };

    const clearTryOnPolls = () => {
        Object.values(tryOnPollsRef.current).forEach(t => clearInterval(t));
        tryOnPollsRef.current = {};
    };

    const pollTryOn = (localId: string, jobId: string) => {
        stopTryOnPoll(localId);
        const tick = async () => {
            try {
                const { data: r } = await widgetAPI.tryonStatus(jobId);
                updateTryOnJob(localId, {
                    status: r.status,
                    stage: r.stage ?? null,
                    still_url: r.still_url ?? null,
                    video_url: r.video_url ?? null,
                    garment_ref: r.garment_ref ?? null,
                    look_ref: r.look_ref ?? null,
                    reason: r.reason ?? null,
                });
                if (r.status === 'completed' || r.status === 'error') {
                    stopTryOnPoll(localId);
                }
            } catch (err) {
                // Transient poll failure — keep polling; persistent failure
                // just leaves the card loading until session reset.
                console.warn('[tryon] poll failed', err);
            }
        };
        tick(); // immediate first poll
        tryOnPollsRef.current[localId] = setInterval(tick, 2000);
    };

    const submitTryOn = async (
        localId: string,
        personS3: string,
        req: Omit<TryOnRequest, 'person_image_s3' | 'agent_id'>,
    ) => {
        if (!agentId) return;
        try {
            const { data } = await widgetAPI.tryon({
                agent_id: agentId,
                person_image_s3: personS3,
                session_id: sessionId || undefined,
                visitor_id: visitorId || undefined,
                product_ids: req.product_ids,
                garment_image_urls: req.garment_image_urls,
                kind: req.kind,
            });
            if (!data?.job_id) {
                updateTryOnJob(localId, { status: 'error' });
                return;
            }
            updateTryOnJob(localId, { jobId: data.job_id, status: 'processing' });
            pollTryOn(localId, data.job_id);
        } catch (err) {
            const status = (err as any)?.response?.status;
            if (status === 429) {
                // Global fashn pool exhausted — drop the card, show a banner.
                setTryOnJobs(prev => prev.filter(j => j.localId !== localId));
                setTryOnNotice('Virtual try-on is temporarily unavailable — please try again later.');
            } else if (status === 403) {
                // Shouldn't happen if button gating is correct; fail quietly.
                setTryOnJobs(prev => prev.filter(j => j.localId !== localId));
            } else {
                updateTryOnJob(localId, { status: 'error' });
            }
        }
    };

    const initiateTryOn = (
        req: Omit<TryOnRequest, 'person_image_s3' | 'agent_id'>,
        refData: Pick<TryOnJob, 'garment_ref' | 'look_ref'>,
    ) => {
        if (!agentId) return;
        setTryOnNotice(null);
        const localId = `tryon_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        setTryOnJobs(prev => [...prev, {
            localId,
            status: 'queued',
            kind: req.kind === 'look' ? 'look' : 'single',
            stage: req.kind === 'look' ? 'Combining your pieces…' : 'Creating your try-on…',
            garment_ref: refData.garment_ref ?? null,
            look_ref: refData.look_ref ?? null,
        }]);
        if (personImageS3) {
            submitTryOn(localId, personImageS3, req);
        } else {
            // Need a photo first — stash the request and open the picker.
            pendingTryOnRef.current = { ...req, localId };
            tryOnFileInputRef.current?.click();
        }
    };

    const startTryOnSingle = (data: ProductCardData) => {
        if (!data.product_id) return;
        // Send the exact variant image the tile is showing so the backend
        // tries on the color the shopper actually saw (not the cache hero).
        // Never send an empty string — omit the field if the image is falsy.
        initiateTryOn(
            {
                product_ids: [String(data.product_id)],
                ...(data.image ? { garment_image_urls: [data.image] } : {}),
                kind: 'single',
            },
            { garment_ref: { product_id: String(data.product_id), title: data.title, image: data.image } },
        );
    };

    const startTryOnLook = (data: LookCardData) => {
        const products = (data.slots || [])
            .map(s => s.product)
            .filter((p): p is ProductCardData => !!p && !!p.product_id);
        const productIds = products.map(p => String(p.product_id));
        if (productIds.length === 0) return;
        // Send each displayed variant image INDEX-ALIGNED with product_ids so
        // the backend pairs each garment with what the shopper saw. Only send
        // the array when EVERY product has an image — a partial array would
        // misalign the pairing, and we must never send an empty-string slot.
        const garmentImages = products.map(p => p.image).filter((img): img is string => !!img);
        const allHaveImages = garmentImages.length === products.length;
        initiateTryOn(
            {
                product_ids: productIds,
                ...(allHaveImages ? { garment_image_urls: garmentImages } : {}),
                kind: 'look',
            },
            { look_ref: { products: products.map(p => ({ product_id: String(p.product_id), title: p.title, image: p.image })) } },
        );
    };

    // Person-photo picker → upload to S3 (reuses uploadAttachment, same path
    // the Stylist photo uses), cache for the session, then run the pending
    // request.
    const handleTryOnFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-selecting the same file later
        const pending = pendingTryOnRef.current;
        pendingTryOnRef.current = null;
        if (!file || !agentId || !visitorId) {
            if (pending) setTryOnJobs(prev => prev.filter(j => j.localId !== pending.localId));
            return;
        }
        try {
            const { data } = await widgetAPI.uploadAttachment(agentId, sessionId, visitorId, file);
            if (!data?.s3_url) throw new Error('no s3_url');
            setPersonImageS3(data.s3_url);
            if (pending) {
                const { localId, ...req } = pending;
                submitTryOn(localId, data.s3_url, req);
            }
        } catch (err) {
            console.warn('[tryon] person photo upload failed', err);
            if (pending) updateTryOnJob(pending.localId, { status: 'error' });
            setTryOnNotice('Could not upload your photo — please try again.');
        }
    };

    // Clear any in-flight try-on polls on unmount (widget teardown / close).
    useEffect(() => () => clearTryOnPolls(), []);

    /** `attachmentsOverride` re-sends already-uploaded attachments verbatim
     *  (error-recovery "Try again"): they are in S3 and `pendingAttachments`
     *  was cleared, so without this a retried photo message would silently
     *  lose its photo. */
    const handleSend = async (messageOverride?: string, attachmentsOverride?: Attachment[]) => {
        const messageText = messageOverride || input;
        if (!messageText.trim() && pendingAttachments.length === 0 && !attachmentsOverride?.length) return;
        if (!agentId || !visitorId) return;

        // Contract-WidgetEmailOTP.md — remember the question in flight. If the
        // backend answers with `verification_required`, this is what we re-send
        // after the visitor verifies. Text only: attachments were already
        // uploaded and consumed by this turn, so a replay must not re-attach
        // them.
        pendingVerifyQuestionRef.current = messageText.trim() || null;

        // Contract-WidgetEmailOTP.md — the "answered in the wrong place" path.
        // The agent asks for the email conversationally, so typing it into the
        // chat box is a correct reading of the question; it just isn't the box
        // that verifies. Remember it and prefill the dialog when the gate opens
        // it, so the visitor is never asked to type the same address twice.
        // The message still sends: the model needs the address in the
        // transcript, and swallowing a customer's message to open a dialog is
        // its own kind of desync. Deliberately not cleared on a non-email turn
        // — an address given three messages ago is still their answer.
        //
        // 🚨 Captured even when this session is ALREADY verified. This used to
        // be guarded by `!verifiedEmail`, which is wrong for the exact case
        // that needs it most: a `different_email` visitor IS verified, and the
        // address they type next is precisely the different one we are asking
        // for. Same trap as the reopen bar's old `!verifiedEmail` condition —
        // verified-ness does not mean "done", it means "done with ONE address".
        // `_verifyPrefill` below decides whether this value is the right thing
        // to show; capturing it is always safe.
        const found = messageText.match(/[^\s@]+@[^\s@]+\.[^\s@]{2,}/);
        if (found) chatTypedEmailRef.current = found[0].replace(/[.,;:!?]+$/, '');

        // Clear any stale handoff-rejection notice once the customer resumes
        // the conversation (Plan-TalkToHumanToggleSchedule.md off-hours UX).
        if (handoffNotice) setHandoffNotice(null);

        // Show the typing dots IMMEDIATELY — before the (potentially multi-
        // second) S3 attachment upload below. Previously `setLoading(true)`
        // ran only after the upload completed, so sending a photo left a
        // silent gap with no dots until the answer returned
        // (Plan-WidgetProgressStages "Also verify"). Reset any stage from a
        // prior turn so the new turn starts clean (the backend re-emits its
        // own "Analyzing your photo…" stage once the turn begins).
        setLoading(true);
        setStatusStage(null);

        // Upload pending attachments first
        let uploadedAttachments: Attachment[] = attachmentsOverride ?? [];
        if (!attachmentsOverride && pendingAttachments.length > 0) {
            const results = await Promise.all(
                pendingAttachments.map(async (pa, index) => {
                    try {
                        const res = await widgetAPI.uploadAttachment(agentId, sessionId, visitorId, pa.file);
                        return { index, data: res.data, error: null };
                    } catch (err) {
                        return { index, data: null, error: 'Upload failed' };
                    }
                })
            );

            const failedUploads = results.filter(r => r.error);
            if (failedUploads.length > 0) {
                // Mark failed thumbnails with error state
                setPendingAttachments(prev =>
                    prev.map((pa, i) => {
                        const result = results.find(r => r.index === i);
                        return result?.error ? { ...pa, error: result.error } : pa;
                    })
                );
                // Upload failed — the turn never starts, so clear the dots we
                // optimistically turned on above (no WS typing:false will fire).
                setLoading(false);
                return; // Don't send if any upload failed
            }

            uploadedAttachments = results.map(r => r.data!);
        }

        const userMessage: Message = {
            role: 'user',
            content: messageText,
            timestamp: new Date().toISOString(),
            attachments: uploadedAttachments.length > 0 ? uploadedAttachments : undefined,
        };

        // Clear pending attachments (revoke object URLs)
        setPendingAttachments(prev => {
            prev.forEach(pa => { if (pa.previewUrl) URL.revokeObjectURL(pa.previewUrl); });
            return [];
        });

        setMessages((prev) => [...prev, userMessage]);
        setInput('');
        // `loading` was already set true at the top of handleSend (so the
        // dots show during the attachment upload); no need to set it again.
        // Reset the WS delivery flag for this turn
        wsDeliveredRef.current = false;

        let gatewayTimeout = false;
        let shouldClearLoading = false;
        try {
            // HTTP response is now immediate (empty messages) — all agent
            // messages are delivered via WebSocket only.
            await widgetAPI.chat(
                agentId,
                userMessage.content,
                visitorId,
                sessionId,
                undefined,
                uploadedAttachments.length > 0 ? uploadedAttachments : undefined,
                channelInfoRef.current.channel === 'shopify_embedded'
                    ? {
                        channel: channelInfoRef.current.channel,
                        shop_domain: channelInfoRef.current.shop_domain,
                        customer_id: channelInfoRef.current.customer_id ?? null,
                        currency: channelInfoRef.current.currency,
                    }
                    : undefined,
                channelInfoRef.current.is_playground === true,
            );
            // The POST was accepted — the turn has started. Whatever happens
            // next arrives over the WS and is not a *send* failure.
            consecutiveSendFailuresRef.current = 0;
        } catch (error) {
            console.error('Chat error:', error);
            const httpStatus = (error as any)?.response?.status;
            const errorDetail = (error as any)?.response?.data?.detail;
            // Plan-EffectiveLimitsAndMaintenanceBanners.md §2b (2026-05-15) —
            // belt-and-suspenders for the race window where the initial
            // `widget/config` returned `chat_available: true` but the
            // workspace exhausted before the POST landed. Backend (per
            // Storm `f12affe`) returns HTTP 402 with detail
            // `chat_limit_exhausted` — or, on the pooled Text allowance,
            // `text_limit_exhausted` (Plan-TextVoicePoolBilling §A4); both
            // are accepted. Flip local maintenance flag so the
            // chat tab swaps to MaintenanceCard on the next render — no
            // config refetch needed. We bail BEFORE the rate-limit /
            // generic-error branches so the user doesn't see a
            // confusing "Something went wrong" toast on top of the
            // maintenance card.
            if (httpStatus === 402 && isChatExhaustedReason(errorDetail)) {
                setChatLimitExhausted(true);
                return;
            }
            if (httpStatus === 504 || httpStatus === 503) {
                // CloudFront timed out the HTTP request, but the backend is still
                // processing and the WebSocket is still alive. The agent's response
                // will arrive via WebSocket. Keep the typing indicator visible and
                // let the WebSocket typing:false signal hide it when done.
                gatewayTimeout = true;
            } else if (error instanceof RateLimitError) {
                const seconds = error.retryAfterSeconds;
                const rateLimitMsg: Message = {
                    role: 'agent',
                    content: seconds
                        ? `You're sending messages too quickly. Please wait ${seconds} seconds before sending another message.`
                        : `You're sending messages too quickly. Please wait a moment and try again.`,
                    timestamp: new Date().toISOString(),
                };
                setMessages((prev) => [...prev, rateLimitMsg]);
                setRateLimitUntil(Date.now() + seconds * 1000);
                setTimeout(() => setRateLimitUntil(0), seconds * 1000);
                // Synthetic error message — WS will NOT fire typing:false for it,
                // so clear the dots inline in finally.
                shouldClearLoading = true;
            } else {
                const isRateLimit = (error as any)?.response?.status === 429 ||
                    (error as any)?.response?.data?.detail?.includes('RESOURCE_EXHAUSTED') ||
                    (error as any)?.response?.data?.detail?.includes('Resource exhausted');

                consecutiveSendFailuresRef.current += 1;
                // Copy no longer says "Please try again" — the bubble now
                // carries a Try again button. It DOES say the message never
                // sent, which is the one thing this branch knows for certain:
                // the POST failed, so the agent turn never started. (The
                // backend's own "I ran into an issue processing your request."
                // means the opposite — the turn started and threw.)
                const errorMsg: Message = {
                    role: 'agent',
                    content: isRateLimit
                        ? "I'm experiencing high demand right now. Your message wasn't sent — give it a moment."
                        : "Something went wrong on my end. Your message wasn't sent.",
                    timestamp: new Date().toISOString(),
                    kind: 'error',
                    errorAttempt: consecutiveSendFailuresRef.current,
                };
                setMessages(prev => [...prev, errorMsg]);
                // Synthetic error message — WS will NOT fire typing:false for it,
                // so clear the dots inline in finally.
                shouldClearLoading = true;
            }
        } finally {
            // Bug fix (2026-05-20): the HTTP POST now ACKs IMMEDIATELY (empty
            // body) — agent reply arrives via WebSocket. Previously this
            // `finally` cleared loading on success, killing the typing dots
            // before the WS `typing:true` signal landed. The fix:
            //   - Error paths (rate-limit, generic) set shouldClearLoading
            //     because the WS won't fire typing:false for synthetic error
            //     messages — clear inline here.
            //   - Gateway-timeout path keeps the dots and trusts WS, as before.
            //   - Success path: do NOT clear immediately; arm a 60s safety
            //     fallback in case WS never delivers (e.g. dead WS). The
            //     happy path is a no-op because wsDeliveredRef flips true
            //     on the agent message at L590.
            if (shouldClearLoading) {
                setLoading(false);
            } else if (!gatewayTimeout) {
                setTimeout(() => {
                    if (!wsDeliveredRef.current) {
                        setLoading(false);
                    }
                }, 60000);
            }
        }
    };

    // Plan-EnterpriseUpsellViaWidget §A.1 — keep handleSendRef pointed at
    // the latest handleSend closure so the once-mounted message listener
    // can call into fresh state (agentId, sessionId, visitorId, ws.current).
    useEffect(() => {
        handleSendRef.current = handleSend;
    });

    // Contract-WidgetEmailOTP.md step 3 — verified ⇒ close the modal, chat
    // continues, and the question that tripped the gate is re-sent
    // automatically so the visitor doesn't retype it. Goes through
    // `handleSendRef` for the same reason the message bridge does: the latest
    // closure, not a stale one.
    const handleVerified = (email: string) => {
        setVerifyModalOpen(false);
        setVerifySessionId(null);
        setVerifyDismissed(false);
        setVerifiedEmail(email);
        // Back to the neutral reason. The address just confirmed is now THE
        // confirmed one, so a stale `different_email` would describe the
        // previous failure, not this state — and the next frame sets the reason
        // afresh anyway.
        setVerifyReason('order_lookup');
        chatTypedEmailRef.current = null;
        const question = pendingVerifyQuestionRef.current;
        pendingVerifyQuestionRef.current = null;
        // 🚨 The desync the owner hit (2026-08-03): the agent asks for the
        // email in prose, the visitor answers in this dialog, and NOTHING the
        // visitor can see changes. The replay below is silent and re-uses the
        // visitor's original wording, so the transcript reads as though they
        // simply repeated themselves and the agent ignored them — and if the
        // agent re-asks (it has no idea the dialog exists; the verified address
        // never reaches the model, only the tool) there is no way to tell a
        // stuck screen from a genuinely new turn.
        //
        // This bubble is the missing receipt. FE-local: the backend transcript
        // never sees it, so it does not survive a reload and is deliberately
        // not a `user` message — it is a statement about the session, not
        // something the visitor said.
        //
        // 🚨 Contract-WidgetEmailOTP.md PINNED rule 3 — the ADDRESS ITSELF must
        // never be written into the chat transcript. The modal is the only
        // place an email or a code is ever shown or collected. An address in
        // the transcript is what let the model hold address A while the
        // visitor verified address B, deny forever, and reopen the modal on
        // every retry — the loop this whole fix exists to kill. So the receipt
        // confirms that the state changed and says nothing about what it
        // changed to; the visitor knows which address they just typed.
        if (mode === 'chat') {
            setMessages((prev) => [
                ...prev,
                {
                    role: 'agent',
                    content: 'Email verified ✓ — thanks! Let me take another look.',
                    timestamp: new Date().toISOString(),
                },
            ]);
        }
        // Chat only — the ref is set by `handleSend`, so during a voice turn it
        // is null and nothing is replayed. Correct: the visitor asked by voice
        // and the live session is still running, so they simply ask again. We
        // must not inject a synthetic text turn into a voice conversation.
        if (question) void handleSendRef.current?.(question);
    };

    const loadHistory = async () => {
        if (!agentId || !visitorId) return;
        setHistoryLoading(true);
        try {
            const response = await widgetAPI.getHistory(agentId, visitorId);
            setHistoryList(response.data.conversations || []);
        } catch (error) {
            console.error('Failed to load history:', error);
            setHistoryList([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const loadConversation = async (targetSessionId: string) => {
        if (!visitorId) return;
        setHistoryLoading(true);
        try {
            const response = await widgetAPI.getConversation(targetSessionId, visitorId, channelInfoRef.current.is_playground === true);
            // Strip storefront tool tokens from persisted history (backend is the
            // canonical fix; this is the safety net for messages already stored
            // from before Storm's backend filter shipped). Drop messages that
            // become empty after stripping.
            const cleaned: Message[] = [];
            for (const m of (response.data.messages || []) as any[]) {
                if (m.kind === 'video' && m.video && m.video.url) {
                    cleaned.push({
                        role: 'agent',
                        content: '',
                        timestamp: m.timestamp,
                        kind: 'video',
                        video: { url: m.video.url, label: m.video.label || '', thumbnail: m.video.thumbnail },
                    });
                    continue;
                }
                const stripped = stripStorefrontTokens(m.content || '');
                const atts = m.attachments;
                if (!stripped && !(atts && atts.length)) continue;
                cleaned.push({
                    role: m.role as 'user' | 'agent',
                    content: stripped,
                    timestamp: m.timestamp,
                    attachments: atts,
                });
            }
            setHistoryMessages(cleaned);
            setHistorySessionId(targetSessionId);
            setHistoryCsatScore(response.data.csat_score ?? null);
            setHistoryTicketId(response.data.ticket_id ?? null);
            setShowInfoPanel(false);
            setMode('history-detail');
        } catch (error) {
            console.error('Failed to load conversation:', error);
        } finally {
            setHistoryLoading(false);
        }
    };

    // Plan-HumanAgentHandoff-2026-06-09 §Customer UI — customer-initiated
    // takeover request. Optimistic flip to 'requested' so the banner
    // renders immediately; backend confirmation arrives on the WS frame.
    // Backend is idempotent (15-min anti-spam), so an in-flight retry
    // (e.g. button double-click) is safe.
    const handleRequestHuman = async () => {
        if (!sessionId || handoffRequesting || handoffStatus !== 'none') return;
        setHandoffRequesting(true);
        setHandoffStatus('requested');
        setHandoffNotice(null);
        try {
            // Pass the widget's agent slug so the BE can create the
            // conversation on-demand on a fresh session (no message sent
            // yet) instead of 404-ing. Same agentId as the chat WS + POST.
            await widgetAPI.requestHuman(sessionId, 'customer_requested', agentId);
        } catch (err) {
            // Roll back optimistic state on hard failure. The BE now gates
            // handoff on the agent's enable toggle + schedule
            // (Plan-TalkToHumanToggleSchedule.md): 403 = handoff disabled,
            // 409 = outside business hours (detail carries the hours label).
            // Surface a friendly notice instead of a silent failure even if a
            // stale widget still showed the button (server is authoritative).
            const status = (err as any)?.response?.status;
            const detail = (err as any)?.response?.data?.detail;
            if (status === 409) {
                setHandoffNotice(
                    typeof detail === 'string' && detail
                        ? detail
                        : "Our team is offline right now. You can keep chatting with our AI assistant here — it can raise a support ticket for the team whenever you need one.",
                );
            } else if (status === 403) {
                setHandoffNotice(
                    typeof detail === 'string' && detail
                        ? detail
                        : "Live chat with our team isn't available right now. You can keep chatting with our AI assistant here — it can raise a support ticket for the team whenever you need one.",
                );
            } else {
                console.warn('[Prodagi] request-human failed:', err);
            }
            setHandoffStatus('none');
        } finally {
            setHandoffRequesting(false);
        }
    };

    // Plan-HumanAgentHandoff §Customer UI — withdraw a still-pending request
    // and resume AI chat. Optimistically flips to 'none' (mirrors the
    // optimistic 'requested' in handleRequestHuman) so the amber banner clears
    // and the "Talk to a live agent" CTA reappears immediately. If a staff
    // member joined in the race, the BE returns status 'active' → keep the
    // human banner rather than stranding the customer. The WS
    // human_handoff_status frame also broadcasts 'none', matching our
    // optimistic set (no fight). Reverts to 'requested' on hard failure.
    const handleCancelHuman = async () => {
        if (!sessionId || handoffCancelling || handoffStatus !== 'requested') return;
        setHandoffCancelling(true);
        setHandoffStatus('none');
        try {
            const res = await widgetAPI.cancelHuman(sessionId, agentId);
            if (res.data?.status === 'active') {
                setHandoffStatus('active');
            }
        } catch (err) {
            console.warn('[Prodagi] cancel-human failed:', err);
            setHandoffStatus('requested');
        } finally {
            setHandoffCancelling(false);
        }
    };

    // `preserveTranscript` (Plan-WidgetReconnect §4, rung 4): mint a fresh
    // session but LEAVE the existing bubbles on screen. Used only by the
    // unrecoverable-connection path, where wiping the transcript would take the
    // customer's context away at the exact moment they are already frustrated.
    // 🚨 Callers must invoke this explicitly — `onClick={handleNewChat}` would
    // pass the MouseEvent as the options object and read as truthy.
    const handleNewChat = ({ preserveTranscript = false }: { preserveTranscript?: boolean } = {}) => {
        // Generate a fresh session, clear messages, go back to chat.
        // Plan-EndConversationUX §B.2 — also clears the `sessionEnded`
        // flag so the "Start new conversation" CTA path lands the user
        // in a working chat (input enabled, no banner). The
        // sessionEpoch bump forces the WS effect to re-run with the
        // fresh sessionId and open a brand-new socket.
        const newSid = newChatSessionId();
        setSessionEnded(false);
        setSessionId(newSid);
        setSessionEpoch((prev) => prev + 1);
        if (!preserveTranscript) {
            setMessages(config ? (config.welcome_message ? [{ role: 'agent', content: config.welcome_message, timestamp: new Date().toISOString() }] : []) : []);
        }
        setMode('chat');
        setLoading(false);
        setStatusStage(null);
        // Fresh session = fresh handoff state — never carry the prior
        // conversation's banner forward.
        setHandoffStatus('none');
        setHandoffStaffName(null);
        // Virtual Try-On is per-session: drop the cached photo, in-flight
        // polls, jobs, and any notice so the new conversation starts clean.
        clearTryOnPolls();
        pendingTryOnRef.current = null;
        setTryOnJobs([]);
        setPersonImageS3(null);
        setTryOnNotice(null);
    };

    // ── Error recovery (Plan-ErrorRecoveryAndLanguageDrift §3) ──────────────
    // After a send failure the WS is still open and the session survives — the
    // `catch` never touches `ws.current` — so "keep chatting" needs no code.
    // These cover the two cases typing again does not: retrying the message
    // that was lost, and escaping a session that keeps failing.

    /** 🔒 Owner decision: drive the existing `create_support_ticket` AI tool.
     *  There is NO anonymous ticket endpoint, and `request-human` would open a
     *  handoff (not a ticket) — and it is gated by the Talk-to-Human toggle and
     *  business hours, so it is dead off-hours. */
    const ERROR_TICKET_PROMPT = 'I hit an error. Please file a support ticket about it.';

    const handleRetryLastMessage = () => {
        // Re-send the last thing the customer actually said. Its attachments are
        // already in S3, so pass them through rather than re-uploading.
        const lastUser = [...messages].reverse().find((m) => m.role === 'user');
        if (!lastUser) return;
        handleSend(lastUser.content, lastUser.attachments);
    };

    const handleAskAiForTicket = () => handleSend(ERROR_TICKET_PROMPT);

    const handleContinueConversation = () => {
        setSessionId(historySessionId);
        setMessages(historyMessages);
        setMode('chat');
    };

    const openHistory = () => {
        if (!config || config.enable_chat_history === false) return;
        loadHistory();
        setMode('history');
    };

    // Isolate Widget Styles from Global App Styles
    useEffect(() => {
        // Save original styles
        const originalBg = document.body.style.background;
        const originalColor = document.body.style.color;

        // Override for Widget
        document.body.style.background = 'transparent';
        document.body.style.color = 'inherit';

        return () => {
            // Restore
            document.body.style.background = originalBg;
            document.body.style.color = originalColor;
        };
    }, []);

    const renderMessageContent = (msg: Message, _idx: number) => {
        if (msg.kind === 'video' && msg.video) {
            return (
                <VideoBubble
                    url={msg.video.url}
                    label={msg.video.label}
                    thumbnail={msg.video.thumbnail}
                    isDark={isDark}
                />
            );
        }
        // Plan-HumanAgentHandoff §Customer UI — when the backend stamps a
        // message with `from_human: true` (staff reply), swap the AI
        // label for the staff member's name so the bubble doesn't lie.
        // Source precedence: per-message `staff_name` (if backend
        // includes it) → conversation-level `handoffStaffName` (resume
        // payload OR the `human_handoff_status === 'active'` WS frame)
        // → "Support" fallback when neither is known (e.g. a released
        // conversation whose by_name field was never persisted).
        const labelText = msg.from_human === true
            ? (msg.staff_name || handoffStaffName || 'Support')
            : config?.ai_label_text;
        // Virtual Try-On (Phase 3) — buttons appear ONLY in the live chat
        // surface when VTO is enabled (master flag AND per-agent effective
        // flag). The admin Inbox / voice panel never pass these callbacks, and
        // the read-only history view (mode !== 'chat') is excluded too.
        const vtoEnabled =
            APP_CONFIG.virtualTryonEnabled &&
            config?.virtual_tryon_enabled === true &&
            mode === 'chat';
        return (
            <AgentMessageContent
                content={msg.content}
                role={msg.role}
                brandColor={activeBrandColor}
                isDark={isDark}
                onArticleClick={(slug) => setArticleViewSlug(slug)}
                aiLabelText={labelText}
                customerLabelText={config?.customer_label_text}
                showAiLabel={config?.show_ai_label !== false}
                showCustomerLabel={config?.show_customer_label !== false}
                onTryOnSingle={vtoEnabled ? startTryOnSingle : undefined}
                onTryOnLook={vtoEnabled ? startTryOnLook : undefined}
            />
        );
    };

    const renderAttachments = (attachments: Attachment[]) => (
        <div className="flex flex-wrap gap-1 mt-1">
            {attachments.map((att, i) =>
                att.type === 'image' ? (
                    <img
                        key={i}
                        src={att.s3_url}
                        alt={att.filename}
                        style={{ maxWidth: '200px', borderRadius: '8px', marginTop: '4px', cursor: 'pointer' }}
                        onClick={() => window.open(att.s3_url, '_blank')}
                    />
                ) : (
                    <a
                        key={i}
                        href={att.s3_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm underline mt-1"
                    >
                        📄 {att.filename}
                    </a>
                )
            )}
        </div>
    );

    if (!config) {
        return <div className="fixed inset-0 flex items-center justify-center bg-transparent">Loading...</div>;
    }

    // Plan-EffectiveLimitsAndMaintenanceBanners.md §2b (2026-05-15) —
    // per-tab maintenance flags. Each is derived from the widget config
    // contract (Phase 1c) AND, for chat only, the WS 1008 close-code
    // override. `undefined` is treated as "available" so pre-Phase-1c
    // backends keep working unchanged. Tabs are INDEPENDENT — voice
    // maintenance does NOT imply chat maintenance, and vice versa. The
    // chat WS close-code override is local-only so the swap is instant;
    // a subsequent config refetch will reaffirm or clear it.
    const chatTabInMaintenance =
        chatLimitExhausted || config.chat_available === false;
    const voiceTabInMaintenance = config.voice_available === false;
    const liveAvatarUnavailable = config.liveavatar_available === false;

    const isDark = theme === 'dark';
    const activeBrandColor = isDark ? (config.brand_color_dark || config.brand_color) : config.brand_color;
    const activeLogoUrl = isDark ? (config.logo_url_dark || config.logo_url) : (config.logo_url_light || config.logo_url);
    const activeAiIconUrl = isDark ? (config.ai_icon_url_dark || config.ai_icon_url_light) : (config.ai_icon_url_light || config.ai_icon_url_dark);

    const activeTextColor = getContrastColor(activeBrandColor);
    const isLightBg = activeTextColor === '#111827';
    const activeBtnClass = isLightBg ? 'bg-black/10 hover:bg-black/20' : 'bg-white/20 hover:bg-white/30';

    // Plan-ComponentEmbed (2026-05-18) — Component Embed mode.
    // SECURITY GATE (not UI-only): `?embed=chat|voice` activates ONLY when
    // the agent is flagged `component_embed_enabled` on the SHIPPED widget
    // config (verified vs back/api/widget.py:395/703). If `?embed=` is set
    // but the flag is false/absent, render a neutral "not enabled" panel —
    // a non-flagged agent NEVER gets a working embedded component.
    const requestedEmbed = embedSurfaceRef.current;
    if (requestedEmbed && config.component_embed_enabled !== true) {
        return (
            <div className={`fixed inset-0 flex items-center justify-center p-6 text-center text-sm ${isDark ? 'bg-black text-gray-400' : 'bg-white text-gray-500'}`}>
                Component embed is not enabled for this agent.
            </div>
        );
    }
    // Flag confirmed true here (else returned above). `isEmbed` drives the
    // chrome-strip; `effectiveMode` force+locks the surface so stray
    // setMode() paths can't switch away from the embedded surface.
    const isEmbed = requestedEmbed !== null;
    const effectiveMode = isEmbed ? requestedEmbed! : mode;

    // Voice Mode View
    if (effectiveMode === 'voice' && agentId) {
        return (
            <div className={`fixed inset-0 flex flex-col overflow-hidden ${isDark ? 'dark bg-black' : 'bg-white'}`}>
                {/* Header — hidden in Component Embed mode: the dev frames
                    this surface in their own page, so no launcher / close /
                    tab / theme chrome. Per-message AI-disclosure label is
                    unaffected (it lives in the message render below). */}
                {!isEmbed && (
                <div
                    className="p-4 shadow-sm flex items-center justify-between transition-colors duration-300"
                    style={{ backgroundColor: activeBrandColor, color: activeTextColor }}
                >
                    <div className="flex items-center space-x-3">
                        {/* Icon Selection Logic */}
                        {(() => {
                            const logoUrl = activeLogoUrl;
                            const headerShape = config.header_logo_shape || 'square';
                            if (logoUrl) {
                                return <img src={logoUrl} alt="Logo" className={headerShape === 'rectangle' ? 'h-8 max-w-[150px] object-contain' : 'w-8 h-8 rounded-full object-cover'} />;
                            }
                            return (
                                <div className={`${headerShape === 'rectangle' ? 'h-8 max-w-[150px]' : 'w-8 h-8 rounded-full p-1'} flex items-center justify-center overflow-hidden ${isLightBg && headerShape !== 'rectangle' ? 'bg-black/10' : ''} ${!isLightBg && headerShape !== 'rectangle' ? 'bg-white/20' : ''}`}>
                                    <img src={headerShape === 'rectangle' ? (isLightBg ? "/logo-purple.png" : "/logo-white.png") : (isLightBg ? "/logo-only-purple.png" : "/logo-only-white.png")} alt="AskProdigy.ai" className="w-full h-full object-contain" />
                                </div>
                            );
                        })()}

                        {config.show_header_text !== false && (
                            <div>
                                {config.show_agent_name !== false && (
                                    <h1 className="font-semibold text-lg leading-tight">{config.agent_name}</h1>
                                )}
                                <p className={config.show_agent_name !== false ? "text-xs opacity-90" : "font-semibold text-lg leading-tight"}>{config.company_name}</p>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center space-x-2">
                        {/* Theme Toggle */}
                        <button
                            onClick={() => setTheme(isDark ? 'light' : 'dark')}
                            className={`p-2 rounded-full transition-colors ${activeBtnClass}`}
                            title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                        >
                            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        </button>

                        {/* Close Button */}
                        <button
                            onClick={() => setMode('chat')}
                            className={`p-2 rounded-full transition-colors ${activeBtnClass}`}
                            title="Return to Chat"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                )}

                {/* Voice Playground — wrapper paints backdrop directly as a
                   sibling of the header, matching the chat-mode pattern.
                   Chat mode works because its messages/input siblings each
                   own an opaque bg at the flex-child level; voice mode used
                   to rely on a deeply nested div inside VoicePlayground,
                   which collapsed under the mobile WebKit flex cascade and
                   exposed whatever was underneath. Putting the backdrop
                   here means even if every child below collapses, the
                   flex-1 box itself paints the backdrop edge-to-edge. */}
                <div
                    className={`flex-1 min-h-0 relative overflow-hidden flex ${isDark ? 'bg-black' : 'bg-white'}`}
                >
                    {voiceTabInMaintenance ? (
                        // Plan-EffectiveLimitsAndMaintenanceBanners.md §2b +
                        // Phase D (2026-05-15): voice budget exhausted
                        // (and overage off) — replace the entire
                        // VoicePlayground (which would otherwise try to
                        // connect the voice WS) with the maintenance
                        // card. Header above (with "Return to Chat" X
                        // button) is untouched so the user can navigate
                        // away. Channel-aware copy:
                        //   - chat up   → "Try Chat tab instead"
                        //   - chat down → "widget under maintenance"
                        // `onSwitchToOther` jumps directly back to chat
                        // without making the user hunt for the X button.
                        <MaintenanceCard
                            companyName={config.company_name}
                            channel={chatTabInMaintenance ? 'all' : 'voice'}
                            otherChannelAvailable={!chatTabInMaintenance}
                            onSwitchToOther={() => setMode('chat')}
                            isDark={isDark}
                            brandColor={activeBrandColor}
                        />
                    ) : (
                        <VoicePlayground
                            agentId={agentId}
                            // Identity passthrough — the ref, not the token, so
                            // the credential never flows through props.
                            identityTokenRef={identityTokenRef}
                            identityRevision={identityRevision}
                            brandColor={activeBrandColor}
                            theme={theme}
                            voiceName={config?.voice_name}
                            avatarEnabled={!!(config?.avatar_enabled && config?.workspace_has_liveavatar_addon)}
                            liveAvatarUnavailable={liveAvatarUnavailable}
                            embedded={isEmbeddedInWidgetJs}
                            // Plan-VoiceCartRoundTrip — real Shopify embed (the
                            // only surface with a same-origin storefront cart and
                            // a widget.js parent bridge). Drives `embedded=1` on
                            // the voice WS so the BE registers the long-running
                            // cart tools. Playground / standalone never qualify.
                            shopifyStorefront={
                                isEmbeddedInWidgetJs &&
                                channelInfoRef.current.channel === 'shopify_embedded'
                            }
                            // Reuse Widget.tsx's existing storefront bridge
                            // (executeViaParent + prodagi-storefront-result
                            // listener + pendingActionsRef) verbatim so the chat
                            // path is byte-for-byte unchanged. VoicePlayground
                            // echoes the result back over its OWN voice WS as a
                            // tool_result frame.
                            onStorefrontAction={runVoiceStorefrontAction}
                            // Contract-WidgetEmailOTP.md — open the OTP modal
                            // over the voice surface, pinned to the VOICE
                            // session id so the verification lands on the
                            // session the gated tool actually runs under.
                            onVerificationRequired={(voiceSessionId, reason) => {
                                setVerifySessionId(voiceSessionId || null);
                                // Same two-value split as the chat frame, and
                                // re-read on every hit for the same reason: a
                                // stale `different_email` surviving into a
                                // later `order_lookup` sends a caller whose
                                // window merely lapsed hunting for an address
                                // they do not need.
                                setVerifyReason(
                                    reason === 'different_email'
                                        ? 'different_email'
                                        : 'order_lookup',
                                );
                                setVerifyModalOpen(true);
                                setVerifyDismissed(false);
                            }}
                            onConversationEnded={(voiceConversationId) => {
                                // Plan-CSATUIModal.md §B.3 follow-up
                                // (2026-05-15). VoicePlayground mints
                                // its OWN random session_id at L243
                                // (`session-<random>`) which doesn't
                                // match the chat WS's FE-minted
                                // `sess_<random>` id, so Storm's
                                // `_mark_conversation_resolved`
                                // broadcast only reaches the voice
                                // WS. This callback bridges the gap.
                                //
                                // The `voiceConversationId` arg comes
                                // from the voice WS's
                                // `conversation_ended` frame
                                // payload (`message.session_id` from
                                // `back/api/voice.py:570`), or a
                                // fallback to the voice WS's minted
                                // id stored in VoicePlayground's
                                // own ref. Either way it's the
                                // conversation doc's primary key in
                                // the DB — the only id Storm's CSAT
                                // endpoint will accept.
                                //
                                // Same gating as the chat-WS handler:
                                // respect `ask_for_csat` (undefined ≡
                                // true) and dedupe via
                                // `csatSubmittedForConv` so a backend
                                // retry / WS reconnect doesn't re-pop
                                // the modal after the user already
                                // responded. VoicePlayground only
                                // calls this AFTER its own 2-second
                                // teardown fallback settles.
                                const convId = voiceConversationId || sessionId;
                                if (
                                    config?.ask_for_csat !== false &&
                                    convId &&
                                    convId !== csatSubmittedForConv
                                ) {
                                    setCsatConversationId(convId);
                                    setCsatModalOpen(true);
                                }
                            }}
                        />
                    )}
                </div>

                {/* Plan-CSATUIModal.md §B.2 — voice-tab CSAT modal.
                    Renders at the voice-mode root level so it overlays
                    the (already torn-down) VoicePlayground surface
                    after `conversation_ended`.

                    Plan-VoiceWidgetRevisions Item 1 (2026-05-25) — the
                    spoken CSAT prompt (browser Web Speech API) is now
                    REMOVED in voice mode. It read awkwardly over the
                    visual star form; users found the double prompt
                    redundant. `useTTS={false}` matches the chat-tab
                    behaviour — visual stars + comment form only, no
                    speech. The CSATModal speak calls were also stripped
                    so no call site can re-introduce TTS. */}
                {csatModalOpen && csatConversationId && (
                    <CSATModal
                        conversationId={csatConversationId}
                        brandColor={activeBrandColor}
                        theme={theme}
                        useTTS={false}
                        onClose={() => {
                            setCsatModalOpen(false);
                            setCsatSubmittedForConv(csatConversationId);
                        }}
                    />
                )}

                {/* Contract-WidgetEmailOTP.md — voice-tab OTP modal. Same
                    component as the chat tab, rendered at the voice-mode root
                    level so it overlays the VoicePlayground surface — the
                    articles/CSAT-modal-in-voice pattern.

                    🚨 The code is TYPED here, never spoken: we do not read
                    codes aloud and never expect spoken entry. The voice session
                    keeps running behind the modal. */}
                {verifyModalOpen && (verifySessionId || sessionId) && agentId && (
                    <EmailVerificationModal
                        sessionId={verifySessionId || sessionId}
                        agentId={agentId}
                        brandColor={activeBrandColor}
                        theme={theme}
                        onVerified={handleVerified}
                        onClose={() => {
                            setVerifyModalOpen(false);
                            setVerifyDismissed(true);
                        }}
                        initialEmail={_verifyPrefill}
                        verifiedEmail={verifiedEmail}
                    reason={verifyReason}
                    />
                )}
            </div>
        );
    }

    // Hoisted out of the JSX so the error bubble can ask whether it is the LAST
    // visible message — recovery actions belong on the live failure, not on a
    // stale one the customer already typed past.
    const visibleChatMessages = messages.filter(
        (m) => m.kind === 'video' || stripStorefrontTokens(m.content || '').length > 0 || (m.attachments && m.attachments.length > 0),
    );

    return (
        <div className={`fixed inset-0 flex flex-col overflow-hidden ${isDark ? 'dark bg-black text-white' : 'bg-white text-gray-900'}`}>
            {/* Header — hidden in Component Embed mode (no launcher / tab
                switcher / history / theme / close chrome; dev frames it in
                their own page). Messages + per-message AI-disclosure label
                + input below are unchanged. */}
            {!isEmbed && (
            <div
                className="p-4 shadow-sm flex items-center justify-between transition-colors duration-300"
                style={{ backgroundColor: activeBrandColor, color: activeTextColor }}
            >
                <div className="flex items-center space-x-3">
                    {/* Icon Selection Logic */}
                    {(() => {
                        const logoUrl = activeLogoUrl;
                        const headerShape = config.header_logo_shape || 'square';
                        if (logoUrl) {
                            return <img src={logoUrl} alt="Logo" className={headerShape === 'rectangle' ? 'h-8 max-w-[150px] object-contain' : 'w-8 h-8 rounded-full object-cover'} />;
                        }
                        return (
                            <div className={`${headerShape === 'rectangle' ? 'h-8 max-w-[150px]' : 'w-8 h-8 rounded-full p-1'} flex items-center justify-center overflow-hidden ${isLightBg && headerShape !== 'rectangle' ? 'bg-black/10' : ''} ${!isLightBg && headerShape !== 'rectangle' ? 'bg-white/20' : ''}`}>
                                <img src={headerShape === 'rectangle' ? (isLightBg ? "/logo-purple.png" : "/logo-white.png") : (isLightBg ? "/logo-only-purple.png" : "/logo-only-white.png")} alt="AskProdigy.ai" className="w-full h-full object-contain" />
                            </div>
                        );
                    })()}

                    {config.show_header_text !== false && (
                        <div>
                            {config.show_agent_name !== false && (
                                <h1 className="font-semibold text-lg leading-tight">{config.agent_name}</h1>
                            )}
                            <p className={config.show_agent_name !== false ? "text-xs opacity-90" : "font-semibold text-lg leading-tight"}>{config.company_name}</p>
                        </div>
                    )}
                </div>

                <div className="flex items-center space-x-2">
                    {/* Theme Toggle */}
                    <button
                        onClick={() => setTheme(isDark ? 'light' : 'dark')}
                        className={`p-2 rounded-full transition-colors ${activeBtnClass}`}
                        title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                    >
                        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                    </button>

                    {/* Chat History Button */}
                    {config.enable_chat_history !== false && (
                    <button
                        onClick={openHistory}
                        className={`p-2 rounded-full transition-colors ${activeBtnClass}`}
                        title="Chat History"
                    >
                        <Clock className="w-5 h-5" />
                    </button>
                    )}

                    {/* Switch to Voice Button (gated by voice_available) */}
                    {config?.enable_voice && (
                        <button
                            onClick={() => {
                                if (config?.voice_available === false) return;
                                setMode('voice');
                            }}
                            disabled={config?.voice_available === false}
                            className={`p-2 rounded-full transition-colors ${activeBtnClass} ${config?.voice_available === false ? 'opacity-40 cursor-not-allowed' : ''}`}
                            title={config?.voice_available === false ? 'Voice minutes exhausted' : 'Switch to Live Voice'}
                            aria-label={config?.voice_available === false ? 'Voice minutes exhausted' : 'Switch to Live Voice'}
                        >
                            <Headphones className="w-5 h-5" />
                        </button>
                    )}
                </div>
            </div>
            )}

            {/* History List View */}
            {config.enable_chat_history !== false && mode === 'history' && (
                <div className={`flex-1 overflow-y-auto ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
                    {/* New Chat button */}
                    <div className="p-3 border-b" style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}>
                        <button
                            onClick={() => handleNewChat()}
                            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium transition-colors"
                            style={{ backgroundColor: activeBrandColor, color: activeTextColor }}
                        >
                            <MessageSquarePlus className="w-4 h-4" />
                            New Chat
                        </button>
                    </div>

                    {historyLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : historyList.length === 0 ? (
                        <div className={`text-center py-12 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            No conversations yet
                        </div>
                    ) : (
                        <div className="divide-y" style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}>
                            {historyList.map((conv) => (
                                <button
                                    key={conv.session_id}
                                    onClick={() => loadConversation(conv.session_id)}
                                    className={`w-full text-left p-4 transition-colors ${isDark ? 'hover:bg-gray-900' : 'hover:bg-gray-100'}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <p className={`text-sm font-medium truncate flex-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                            {conv.preview || 'No message'}
                                        </p>
                                        <span className={`text-xs flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                            {conv.message_count} msg{conv.message_count !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {formatServerDate(conv.created_at, undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                        {' \u00b7 '}
                                        {formatServerTime(conv.created_at, undefined, { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* History Detail View (read-only past conversation) */}
            {config.enable_chat_history !== false && mode === 'history-detail' && (
                <div className={`flex-1 overflow-y-auto flex flex-col ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
                    {/* Back bar */}
                    <div className="p-3 border-b flex items-center gap-2" style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}>
                        <button
                            onClick={() => { setMode('history'); loadHistory(); }}
                            className={`p-1.5 rounded-full transition-colors ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                            title="Back to history"
                        >
                            <ArrowLeft className="w-4 h-4" />
                        </button>
                        <span className={`text-xs flex-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Past conversation</span>
                        <button
                            onClick={() => setShowInfoPanel(!showInfoPanel)}
                            className={`p-1.5 rounded-full transition-colors ${showInfoPanel ? (isDark ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-900') : (isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-200 text-gray-500')}`}
                            title="More Info"
                        >
                            <Info className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Info Panel */}
                    {showInfoPanel && (
                        <div className={`mx-3 mt-2 p-3 rounded-xl text-sm border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                            <div className="flex items-center justify-between py-1.5">
                                <span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>CSAT Score</span>
                                <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {historyCsatScore != null ? historyCsatScore : 'N/A'}
                                </span>
                            </div>
                            <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`} />
                            <div className="flex items-center justify-between py-1.5">
                                <span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Ticket ID</span>
                                <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {historyTicketId || 'N/A'}
                                </span>
                            </div>
                        </div>
                    )}

                    {historyLoading ? (
                        <div className="flex items-center justify-center py-12 flex-1">
                            <div className="w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : (
                        <>
                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                {historyMessages
                                    .filter((m) => m.kind === 'video' || stripStorefrontTokens(m.content || '').length > 0 || (m.attachments && m.attachments.length > 0))
                                    .map((msg, idx) => (
                                    <div
                                        key={idx}
                                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div
                                            className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm shadow-sm ${msg.role === 'user'
                                                ? ''
                                                : isDark
                                                    ? 'bg-gray-800 border border-gray-700'
                                                    : 'bg-white border border-gray-100'
                                                }`}
                                            style={msg.role === 'user'
                                                ? isDark
                                                    ? { backgroundColor: 'white', color: '#111827', border: `2px solid ${activeBrandColor}` }
                                                    : { backgroundColor: activeBrandColor, color: config.customer_font_color || '#ffffff' }
                                                : { color: isDark ? '#ffffff' : '#000000' }
                                            }
                                        >
                                            <div className={`prose prose-sm max-w-none flex-1 min-w-0 [overflow-wrap:anywhere] ${isDark && msg.role !== 'user' ? 'prose-invert' : ''}`} style={{ color: msg.role === 'user' ? 'inherit' : (isDark ? '#e5e7eb' : '#374151') }}>
                                                {renderMessageContent(msg, idx)}
                                            </div>
                                            {/* Attachments */}
                                            {msg.attachments && msg.attachments.length > 0 && renderAttachments(msg.attachments)}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Resume / New Chat CTAs at bottom */}
                            <div className="p-3 border-t flex gap-2" style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}>
                                <button
                                    onClick={handleContinueConversation}
                                    className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium transition-colors"
                                    style={{ backgroundColor: activeBrandColor, color: activeTextColor }}
                                >
                                    <RotateCcw className="w-4 h-4" />
                                    Continue
                                </button>
                                <button
                                    onClick={() => handleNewChat()}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium transition-colors border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                                >
                                    <MessageSquarePlus className="w-4 h-4" />
                                    New Chat
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Inline Article Viewer */}
            {mode === 'chat' && articleViewSlug && agentId && (
                <div className="flex-1 min-h-0 relative overflow-hidden">
                    <HelpCenterArticleView
                        agentId={agentId}
                        slug={articleViewSlug}
                        brandColor={activeBrandColor}
                        isDark={isDark}
                        onBack={() => setArticleViewSlug(null)}
                    />
                </div>
            )}

            {/* Plan-EffectiveLimitsAndMaintenanceBanners.md §2b — chat tab
                maintenance card. When the workspace's chat budget is
                exhausted (and auto-overage is off), OR the chat WS just
                closed with code 1008 reason `chat_limit_exhausted` /
                `text_limit_exhausted`, we
                replace the message stream with a maintenance card. The
                input row below is rendered separately and stays VISIBLE
                but DISABLED (per spec) so the user still sees the
                surface they expected — just inert. Voice tab is
                unaffected (independent budget per Phase 1c). */}
            {mode === 'chat' && !articleViewSlug && chatTabInMaintenance && (
                // Plan-EffectiveLimitsAndMaintenanceBanners.md §2b +
                // Phase D (2026-05-15): channel-aware copy. When voice
                // is still available, the card suggests switching to
                // voice; when both are down it falls through to the
                // generic "widget under maintenance" headline. The tab
                // bar (Switch to Voice header button) remains visible
                // independently — the card's inline switch suggestion
                // is a convenience shortcut in mid-body.
                <MaintenanceCard
                    companyName={config.company_name}
                    channel={voiceTabInMaintenance ? 'all' : 'chat'}
                    otherChannelAvailable={!voiceTabInMaintenance}
                    onSwitchToOther={() => setMode('voice')}
                    isDark={isDark}
                    brandColor={activeBrandColor}
                />
            )}

            {/* Plan-HumanAgentHandoff-2026-06-09 §Customer UI — handoff
                banners. Rendered ABOVE the message scroll area so the
                customer sees it without scrolling. Three variants:
                - active (green): a staff member has joined
                - requested (amber): waiting for staff pickup
                Mobile 375px-safe (truncate staff name, full-width).
                Dark + light variants kept legible against
                bg-gray-50 / bg-black wrappers. */}
            {mode === 'chat' && !articleViewSlug && !chatTabInMaintenance && handoffStatus === 'active' && (
                <div
                    className={`px-3 py-2 flex items-center gap-2 text-xs font-medium border-b ${
                        isDark
                            ? 'bg-emerald-900/40 border-emerald-800 text-emerald-100'
                            : 'bg-emerald-50 border-emerald-200 text-emerald-900'
                    }`}
                >
                    <UserCheck size={14} className="flex-shrink-0" />
                    <span className="truncate">
                        {handoffStaffName
                            ? `${handoffStaffName} has joined this conversation`
                            : 'A human agent has joined this conversation'}
                    </span>
                </div>
            )}
            {mode === 'chat' && !articleViewSlug && !chatTabInMaintenance && handoffStatus === 'requested' && (
                <div
                    className={`px-3 py-2 flex items-center gap-2 text-xs font-medium border-b ${
                        isDark
                            ? 'bg-amber-900/40 border-amber-800 text-amber-100'
                            : 'bg-amber-50 border-amber-200 text-amber-900'
                    }`}
                >
                    <Loader2 size={14} className="flex-shrink-0 animate-spin" />
                    <span className="truncate">
                        Connecting you to our team — someone will be with you shortly
                    </span>
                    <button
                        type="button"
                        onClick={handleCancelHuman}
                        disabled={handoffCancelling}
                        title="Cancel and return to the AI assistant"
                        aria-label="Cancel human handoff request"
                        className={`ml-auto flex-shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            isDark
                                ? 'bg-amber-800/60 hover:bg-amber-800 text-amber-50'
                                : 'bg-amber-100 hover:bg-amber-200 text-amber-900'
                        }`}
                    >
                        <X size={12} />
                        <span>Cancel</span>
                    </button>
                </div>
            )}

            {/* Plan-WidgetReconnect §4 — connection affordance.

                🚨 NON-BLOCKING BY CONSTRUCTION. This is a thin banner only; the
                composer stays live underneath, because turns travel by HTTP
                POST and still work perfectly while the socket is down — only
                the REPLY delivery is affected. Blocking input would break a
                path that isn't broken.

                🚨 NEVER SAY "REFRESH THE PAGE". `PERSISTED_SESSION_TTL_MS` is
                10 minutes, so after the ~30-minute idle that causes this, a
                refresh mints a NEW session and silently destroys the thread we
                are recovering — the opposite of the fix.

                Hidden entirely while `sessionEnded` so a resolved conversation
                never shows connection chrome. */}
            {mode === 'chat' && !articleViewSlug && !chatTabInMaintenance && !sessionEnded && connState !== 'live' && (
                <div
                    role="status"
                    aria-live="polite"
                    className={`px-3 py-2 flex gap-2 text-xs font-medium border-b ${
                        connState === 'retrying' ? 'items-center' : 'items-start'
                    } ${
                        connState === 'retrying'
                            ? isDark
                                ? 'bg-slate-800/60 border-slate-700 text-slate-200'
                                : 'bg-slate-50 border-slate-200 text-slate-700'
                            : isDark
                                ? 'bg-amber-900/40 border-amber-800 text-amber-100'
                                : 'bg-amber-50 border-amber-200 text-amber-900'
                    }`}
                >
                    {connState === 'retrying' ? (
                        <>
                            <Loader2 size={14} className="flex-shrink-0 animate-spin" />
                            <span className="truncate">Reconnecting…</span>
                        </>
                    ) : (
                        <>
                            <WifiOff size={14} className="flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="leading-snug">
                                    Connection lost — your messages still send.
                                </p>
                                {/* Rungs 3 and 4. Both are BUTTONS inside the
                                    widget, never an instruction to reload.
                                    Stacked so they stay tappable at 375px. */}
                                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                    <button
                                        type="button"
                                        onClick={() => manualRetryRef.current?.()}
                                        title="Try to reconnect"
                                        aria-label="Retry connection"
                                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                                            isDark
                                                ? 'bg-amber-800/60 hover:bg-amber-800 text-amber-50'
                                                : 'bg-amber-100 hover:bg-amber-200 text-amber-900'
                                        }`}
                                    >
                                        <RotateCcw size={12} />
                                        <span>Retry</span>
                                    </button>
                                    {/* 🚨 Rung 4 — the last resort, and it
                                        PRESERVES THE TRANSCRIPT. Mints a fresh
                                        session client-side (no reload), so the
                                        customer keeps every bubble of context
                                        while getting a working socket. This is
                                        the in-widget replacement for "refresh
                                        the page", which would be actively
                                        destructive: PERSISTED_SESSION_TTL_MS is
                                        10 minutes, so a reload after the ~30
                                        minute idle that caused this starts a
                                        NEW session and loses the thread. */}
                                    <button
                                        type="button"
                                        onClick={() => handleNewChat({ preserveTranscript: true })}
                                        title="Start a new chat, keeping this conversation on screen"
                                        aria-label="Start a new chat"
                                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                                            isDark
                                                ? 'bg-amber-800/60 hover:bg-amber-800 text-amber-50'
                                                : 'bg-amber-100 hover:bg-amber-200 text-amber-900'
                                        }`}
                                    >
                                        <MessageSquarePlus size={12} />
                                        <span>Start a new chat</span>
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Messages */}
            {mode === 'chat' && !articleViewSlug && !chatTabInMaintenance && <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
                {visibleChatMessages
                    .map((msg, idx) => {
                    const showErrorActions = msg.kind === 'error' && idx === visibleChatMessages.length - 1;
                    return (
                        <div
                            key={idx}
                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                            <div
                                className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm shadow-sm ${msg.role === 'user'
                                    ? ''
                                    : isDark
                                        ? 'bg-gray-800 border border-gray-700'
                                        : 'bg-white border border-gray-100'
                                    }`}
                                style={msg.role === 'user'
                                    ? isDark
                                        ? { backgroundColor: 'white', color: '#111827', border: `2px solid ${activeBrandColor}` }
                                        : { backgroundColor: activeBrandColor, color: config.customer_font_color || '#ffffff' }
                                    : { color: isDark ? '#ffffff' : '#000000' }
                                }
                            >
                                {/* Icon + Content flex row — DOM order controls left/right */}
                                <div
                                    className={`flex items-start ${msg.role !== 'user'
                                        ? (config.ai_icon_position === 'right' ? 'gap-1' : 'gap-2')
                                        : (config.customer_icon_position === 'right' ? 'gap-1' : 'gap-2')
                                        }`}
                                >
                                    {/* AI Icon — LEFT position.
                                        Plan-HumanAgentHandoff §Customer UI —
                                        when a staff message is rendered
                                        (from_human), swap the AI badge for a
                                        person icon so the customer can tell
                                        at a glance who's replying. */}
                                    {msg.role !== 'user' && config.show_ai_icon !== false && config.ai_icon_position !== 'right' && (
                                        <span className={`w-9 h-9 rounded-full flex-shrink-0 inline-flex items-center justify-center overflow-hidden mt-0.5 ${
                                            msg.from_human
                                                ? (isDark ? 'bg-emerald-700' : 'bg-emerald-100')
                                                : (isDark ? 'bg-gray-700' : 'bg-white')
                                        }`}>
                                            {msg.from_human ? (
                                                <UserCheck size={14} className={isDark ? 'text-white' : 'text-emerald-700'} />
                                            ) : activeAiIconUrl ? (
                                                <img src={activeAiIconUrl} alt="AI" className="w-full h-full object-cover" />
                                            ) : (
                                                <span className={`text-[8px] font-bold ${isDark ? 'text-gray-300' : 'text-blue-600'}`}>AI</span>
                                            )}
                                        </span>
                                    )}
                                    {/* Customer Icon — LEFT position */}
                                    {msg.role === 'user' && config.show_customer_icon !== false && config.customer_icon_position !== 'right' && (
                                        <span className={`w-9 h-9 rounded-full flex-shrink-0 inline-flex items-center justify-center overflow-hidden mt-0.5 ${isDark ? 'bg-gray-200' : 'bg-white'}`}>
                                            {config.customer_icon_url ? (
                                                <img src={config.customer_icon_url} alt="User" className="w-full h-full object-cover" />
                                            ) : (
                                                <span className={`text-[8px] font-bold text-gray-700`}>U</span>
                                            )}
                                        </span>
                                    )}
                                    {/* Message text (with Help Center block support) */}
                                    <div className={`prose prose-sm max-w-none flex-1 min-w-0 [overflow-wrap:anywhere] ${isDark && msg.role !== 'user' ? 'prose-invert' : ''}`} style={{ color: msg.role === 'user' ? 'inherit' : (isDark ? '#e5e7eb' : '#374151') }}>
                                        {renderMessageContent(msg, idx)}
                                    </div>
                                    {/* AI Icon — RIGHT position.
                                        Same human-vs-AI swap as the LEFT
                                        branch above. */}
                                    {msg.role !== 'user' && config.show_ai_icon !== false && config.ai_icon_position === 'right' && (
                                        <span className={`w-9 h-9 rounded-full flex-shrink-0 inline-flex items-center justify-center overflow-hidden mt-0.5 ${
                                            msg.from_human
                                                ? (isDark ? 'bg-emerald-700' : 'bg-emerald-100')
                                                : (isDark ? 'bg-gray-700' : 'bg-white')
                                        }`}>
                                            {msg.from_human ? (
                                                <UserCheck size={14} className={isDark ? 'text-white' : 'text-emerald-700'} />
                                            ) : activeAiIconUrl ? (
                                                <img src={activeAiIconUrl} alt="AI" className="w-full h-full object-cover" />
                                            ) : (
                                                <span className={`text-[8px] font-bold ${isDark ? 'text-gray-300' : 'text-blue-600'}`}>AI</span>
                                            )}
                                        </span>
                                    )}
                                    {/* Customer Icon — RIGHT position */}
                                    {msg.role === 'user' && config.show_customer_icon !== false && config.customer_icon_position === 'right' && (
                                        <span className={`w-9 h-9 rounded-full flex-shrink-0 inline-flex items-center justify-center overflow-hidden mt-0.5 ${isDark ? 'bg-gray-200' : 'bg-white'}`}>
                                            {config.customer_icon_url ? (
                                                <img src={config.customer_icon_url} alt="User" className="w-full h-full object-cover" />
                                            ) : (
                                                <span className={`text-[8px] font-bold text-gray-700`}>U</span>
                                            )}
                                        </span>
                                    )}
                                </div>
                                {/* Attachments */}
                                {msg.attachments && msg.attachments.length > 0 && renderAttachments(msg.attachments)}

                                {/* Error-recovery actions (Plan-ErrorRecoveryAndLanguageDrift §3).
                                    In-flow inside the bubble — no fixed/absolute chrome, no
                                    z-index. Only on the LAST visible bubble: once the customer
                                    has typed past a failure, its actions are stale.
                                    "Keep chatting" is deliberately absent — the WS survived the
                                    error, so the input below already works. */}
                                {showErrorActions && (() => {
                                    const actionsDisabled = loading || sessionEnded || chatTabInMaintenance || Date.now() < rateLimitUntil;
                                    // A retry has already failed ⇒ the agent is probably the thing
                                    // that's down, so asking IT to file a ticket is the weakest of
                                    // the three. Keep it reachable, stop advertising it.
                                    const ticketDeEmphasized = (msg.errorAttempt ?? 1) >= 2;
                                    const canRetry = messages.some((m) => m.role === 'user');
                                    return (
                                        <div className={`mt-2.5 pt-2.5 border-t flex flex-col gap-1.5 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                            <div className="flex flex-wrap gap-1.5">
                                                {canRetry && (
                                                    <button
                                                        type="button"
                                                        onClick={handleRetryLastMessage}
                                                        disabled={actionsDisabled}
                                                        className="flex-1 min-w-[7rem] inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                                                        // A pale brand color needs dark text — same helper the
                                                        // send button uses, rather than a hardcoded white.
                                                        style={{ backgroundColor: activeBrandColor, color: activeTextColor }}
                                                    >
                                                        <RotateCcw size={13} className="flex-shrink-0" />
                                                        Try again
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => handleNewChat()}
                                                    disabled={chatTabInMaintenance}
                                                    className={`flex-1 min-w-[7rem] inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                                        isDark
                                                            ? 'border-gray-600 text-gray-100 hover:bg-gray-700'
                                                            : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                                                    }`}
                                                >
                                                    <MessageSquarePlus size={13} className="flex-shrink-0" />
                                                    Start a new chat
                                                </button>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleAskAiForTicket}
                                                disabled={actionsDisabled}
                                                className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                                    ticketDeEmphasized
                                                        ? (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
                                                        : (isDark
                                                            ? 'font-medium border border-gray-600 text-gray-100 hover:bg-gray-700'
                                                            : 'font-medium border border-gray-300 text-gray-700 hover:bg-gray-100')
                                                }`}
                                            >
                                                <LifeBuoy size={13} className="flex-shrink-0" />
                                                Ask the AI to file a ticket
                                            </button>
                                            {ticketDeEmphasized && (
                                                <p className={`text-[11px] leading-snug ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    That didn't go through either. Starting a new chat is the most
                                                    likely to work right now.
                                                </p>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    );
                })}
                {/* Virtual Try-On (Phase 3) — result cards stream in at the
                    bottom of the conversation, progressive (still → spin
                    video). Only populated when VTO is enabled and a try-on
                    button was tapped. */}
                {tryOnJobs.map((job) => (
                    <div key={job.localId} className="flex justify-start">
                        <div className="max-w-[85%] min-w-0">
                            <TryOnCard job={job} brandColor={activeBrandColor} isDark={isDark} />
                        </div>
                    </div>
                ))}
                {/* Pool-exhausted / upload-error notice for try-on. */}
                {tryOnNotice && (
                    <div className="flex justify-center pt-1">
                        <div
                            className={`text-xs text-center rounded-lg px-3 py-2 max-w-[85%] ${
                                isDark ? 'bg-gray-800 text-gray-300 border border-gray-700' : 'bg-gray-100 text-gray-600 border border-gray-200'
                            }`}
                        >
                            {tryOnNotice}
                        </div>
                    </div>
                )}
                {/* Plan-StylistFlowV2.md — welcome-screen "Be my Stylist"
                    trigger. Discovery aid: customers don't know the Stylist
                    exists and don't reliably phrase the trigger, so a one-tap
                    button posts the canonical trigger message for them.
                    Gated FE-side on the per-agent effective flag
                    (config.stylist_enabled). That effective flag is a
                    TRIPLE gate computed backend-side: agent toggle AND active
                    Shopify install AND a COMPLETED catalog sync — so the button
                    auto-hides until the merchant syncs their catalog (see
                    Plan-StylistCatalogSync.md; the Stylist needs the synced
                    mirror to build looks). No FE logic change needed here.
                    Welcome-state ONLY — hidden the moment the shopper sends
                    anything (no user-role message yet), and never during an
                    ended session, a human handoff, or while a reply is loading. */}
                {config.stylist_enabled
                    && !messages.some((m) => m.role === 'user')
                    && !sessionEnded
                    && handoffStatus === 'none'
                    && !loading && (
                    <div className="flex justify-center pt-1">
                        <button
                            type="button"
                            onClick={() => handleSend("I'd like you to be my personal stylist")}
                            className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold shadow-sm transition-opacity hover:opacity-90 active:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 max-w-full"
                            style={{
                                backgroundColor: activeBrandColor,
                                color: getContrastColor(activeBrandColor),
                                ['--tw-ring-color' as any]: activeBrandColor,
                                ['--tw-ring-offset-color' as any]: isDark ? '#000000' : '#f9fafb',
                            }}
                            aria-label="Be my Stylist — start a personalized look"
                        >
                            <Sparkles size={16} className="flex-shrink-0" />
                            <span className="truncate">Be my Stylist</span>
                        </button>
                    </div>
                )}
                {/* Plan-HumanAgentHandoff §Customer UI — suppress the
                    "AI is thinking..." dots when a human is handling
                    the conversation. Staff messages aren't AI typing. */}
                {loading && handoffStatus !== 'active' && (
                    <div className="flex justify-start">
                        <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} border rounded-2xl px-4 py-2 shadow-sm max-w-[85%]`}>
                            {/* Plan-WidgetProgressStages — when the backend has
                                streamed a process stage, show it as a small
                                muted line above the dots. When `statusStage` is
                                null this branch is skipped and the dots render
                                exactly as before (no chat-typing regression). */}
                            {statusStage && (
                                <div className={`flex items-center gap-1.5 mb-1.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    <Sparkles size={12} className="flex-shrink-0 opacity-70" />
                                    <span className="leading-snug break-words">{statusStage}</span>
                                </div>
                            )}
                            <div className="flex space-x-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>}

            {/* Plan-EndConversationUX §B.2 — ended-state banner +
                "Start new conversation" CTA. Rendered inline above the
                (disabled) input so the transcript above stays visible.
                Light + dark, mobile-first (full-width, no wrap). The
                button calls `handleNewChat()` which clears
                `sessionEnded`, mints a fresh sessionId, bumps the
                sessionEpoch (forces WS reconnect), and resets messages
                to the welcome state. */}
            {mode === 'chat' && !articleViewSlug && !chatTabInMaintenance && sessionEnded && (
                <div
                    className={`px-3 py-3 border-t ${
                        isDark
                            ? 'bg-gray-900 border-gray-700 text-gray-200'
                            : 'bg-gray-50 border-gray-200 text-gray-700'
                    }`}
                >
                    <p className="text-xs mb-2 leading-snug">
                        Conversation resolved by AI. Start a new conversation
                        if you need more help.
                    </p>
                    <button
                        type="button"
                        onClick={() => handleNewChat()}
                        className="w-full py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition-transform hover:scale-[1.01]"
                        style={{ backgroundColor: activeBrandColor }}
                    >
                        Start new conversation
                    </button>
                </div>
            )}

            {/* Plan-HumanAgentHandoff-2026-06-09 §Customer UI — "Talk to
                a live agent" CTA. Renders ABOVE the input row so it doesn't
                fight with the mic / send buttons at 375px. Only shown
                when status === 'none' (no pending request, no active
                human); hidden the moment the customer clicks (state
                flips to 'requested' optimistically, banner takes over).
                Also hidden once the session is ended or chat is in
                maintenance — both paths already block the input. */}
            {/* Talk-to-Human gate (Plan-TalkToHumanToggleSchedule.md).
                `human_handoff_enabled === false` → nothing renders here at all
                (no handoff feature, so neither branch has anything to say).
                Otherwise the two branches are INDEPENDENT, each with its own
                presentation-only flag:

                  · IN hours (`human_handoff_available !== false`) → the "Talk
                    to a live agent" button, gated by
                    `human_handoff_button_visible`.
                  · OFF hours (`human_handoff_available === false`) → the "Our
                    team is available …" notice, gated by
                    `human_handoff_hours_visible` — and by NOTHING else.

                The notice is NOT an apology for a missing button. It tells the
                customer two things worth saying on their own: the human team is
                offline right now, and the AI can still raise a support ticket
                for them. Both stay true when the client hides the self-service
                button, so `human_handoff_button_visible === false` must not
                suppress it. A client who does not want their staffing schedule
                shown to customers turns off `human_handoff_hours_visible`
                instead — that is the flag that means "say nothing".

                All three flags default to VISIBLE when undefined (back-compat:
                `!== false`, never a truthy check — existing agents that never
                set them keep today's behaviour). The server re-enforces
                availability at request time; these booleans are UI-only. When a
                branch is suppressed we render `null`, never an empty bordered
                div. The backend also omits `human_handoff_hours_label` when
                `human_handoff_hours_visible` is false, and the label ternary
                below already copes with a missing label. */}
            {mode === 'chat'
                && !articleViewSlug
                && !chatTabInMaintenance
                && !sessionEnded
                && handoffStatus === 'none'
                && config?.human_handoff_enabled !== false && (
                config?.human_handoff_available !== false ? (
                    config?.human_handoff_button_visible !== false ? (
                        <div
                            className={`px-3 pt-2 pb-1 border-t ${
                                isDark ? 'border-gray-800' : 'border-gray-200'
                            }`}
                            style={{ backgroundColor: activeBrandColor }}
                        >
                            <button
                                type="button"
                                onClick={handleRequestHuman}
                                disabled={handoffRequesting}
                                className="w-full flex items-center justify-center gap-2 text-xs font-medium py-1.5 rounded-full bg-white/15 hover:bg-white/25 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                style={{ color: activeTextColor }}
                                title="Talk to a live agent"
                            >
                                <Hand size={14} />
                                <span>Talk to a live agent</span>
                            </button>
                        </div>
                    ) : null
                ) : config?.human_handoff_hours_visible !== false ? (
                    <div
                        className={`px-3 pt-2 pb-1 border-t ${
                            isDark ? 'border-gray-800' : 'border-gray-200'
                        }`}
                        style={{ backgroundColor: activeBrandColor }}
                    >
                        <div
                            className="w-full flex items-center justify-center gap-1.5 text-[11px] font-medium leading-snug text-center py-1"
                            style={{ color: activeTextColor, opacity: 0.9 }}
                        >
                            <Clock size={12} className="flex-shrink-0" />
                            <span>
                                {config?.human_handoff_hours_label
                                    ? `Our team is available ${config.human_handoff_hours_label}. You can keep chatting with our AI assistant here — it can raise a support ticket for the team whenever you need one.`
                                    : `Our team is offline right now. You can keep chatting with our AI assistant here — it can raise a support ticket for the team whenever you need one.`}
                            </span>
                        </div>
                    </div>
                ) : null
            )}

            {/* Handoff rejection notice — server rejected a request (403/409),
                e.g. a stale button clicked after hours. Cleared when the
                customer sends their next message. */}
            {mode === 'chat' && !articleViewSlug && !chatTabInMaintenance && handoffNotice && (
                <div
                    className={`px-3 pt-2 pb-1 border-t ${
                        isDark ? 'border-gray-800' : 'border-gray-200'
                    }`}
                    style={{ backgroundColor: activeBrandColor }}
                >
                    <div
                        className="w-full flex items-center justify-center gap-1.5 text-[11px] font-medium leading-snug text-center py-1"
                        style={{ color: activeTextColor, opacity: 0.9 }}
                    >
                        <Info size={12} className="flex-shrink-0" />
                        <span>{handoffNotice}</span>
                    </div>
                </div>
            )}

            {/* Contract-WidgetEmailOTP.md — the way back in. Dismissing the
                dialog used to be a dead end: the gate is server-side so nothing
                breaks, but the only route back was to re-ask a question and
                hope the gate tripped again, which reads as the widget having
                forgotten. Shown only while the visitor is dismissed AND
                unverified, so it disappears the moment either changes.
                Reopens the same modal — no second flow to keep in sync. */}
            {mode === 'chat' && !articleViewSlug && !chatTabInMaintenance
                && verifyDismissed && !verifyModalOpen
                && (!verifiedEmail || verifyReason === 'different_email') && (
                <OtpVerifyBar
                    brandColor={activeBrandColor}
                    textColor={activeTextColor}
                    isDark={isDark}
                    reason={verifyReason}
                    onVerify={() => {
                        setVerifyModalOpen(true);
                        setVerifyDismissed(false);
                    }}
                />
            )}

            {/* Input */}
            {mode === 'chat' && !articleViewSlug && <div className="p-3" style={{ backgroundColor: activeBrandColor }}>
                {/* Pending Attachments Preview */}
                {pendingAttachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                        {pendingAttachments.map((pa, idx) => (
                            <div key={idx} className="relative flex-shrink-0">
                                {pa.type === 'image' ? (
                                    <div className={`w-12 h-12 rounded-lg overflow-hidden border-2 ${pa.error ? 'border-red-500' : 'border-white/40'}`}>
                                        <img src={pa.previewUrl} alt="attachment" className="w-full h-full object-cover" />
                                        {pa.error && (
                                            <div className="absolute inset-0 bg-red-500/70 flex items-center justify-center rounded-lg">
                                                <span className="text-white text-[8px] text-center px-0.5">{pa.error}</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs max-w-[120px] truncate ${pa.error ? 'bg-red-500/70' : 'bg-black/20'}`} style={{ color: activeTextColor }}>
                                        <span>📄</span>
                                        <span className="truncate">{pa.file.name}</span>
                                        {pa.error && <span className="text-red-200 ml-1">!</span>}
                                    </div>
                                )}
                                <button
                                    onClick={() => removePendingAttachment(idx)}
                                    className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-gray-700 text-white flex items-center justify-center hover:bg-gray-900"
                                    title="Remove"
                                >
                                    <X size={10} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex items-center space-x-2">
                    {/* Auto-read Toggle */}
                    <button
                        onClick={() => {
                            const newState = !isAutoReadEnabled;
                            setIsAutoReadEnabled(newState);
                            if (!newState) window.speechSynthesis.cancel();
                        }}
                        className={`p-2 rounded-full transition-colors flex-shrink-0 ${isAutoReadEnabled ? 'bg-black/20' : 'opacity-70 hover:opacity-100'} disabled:opacity-40 disabled:cursor-not-allowed`}
                        style={{ color: activeTextColor }}
                        title={isAutoReadEnabled ? 'Auto-read On' : 'Auto-read Off'}
                        disabled={sessionEnded || chatTabInMaintenance}
                    >
                        {isAutoReadEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
                    </button>

                    {/* Mic Button */}
                    <button
                        onClick={handleVoiceInput}
                        className={`p-2 rounded-full transition-colors flex-shrink-0 ${isListening ? 'bg-red-500 animate-pulse' : 'opacity-70 hover:opacity-100'} disabled:opacity-40 disabled:cursor-not-allowed`}
                        style={{ color: isListening ? '#ffffff' : activeTextColor }}
                        title="Voice Input"
                        disabled={sessionEnded || chatTabInMaintenance}
                    >
                        <Mic size={20} />
                    </button>

                    {/* Attachment Button */}
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="p-2 rounded-full transition-colors flex-shrink-0 opacity-70 hover:opacity-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ color: activeTextColor }}
                        title="Attach file"
                        disabled={(loading && handoffStatus !== 'active') || sessionEnded || chatTabInMaintenance}
                    >
                        <Paperclip size={20} />
                    </button>

                    {/* Hidden File Input */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,.pdf,.docx,.txt"
                        multiple
                        className="hidden"
                        onChange={handleFileSelect}
                    />

                    {/* Virtual Try-On (Phase 3) — dedicated hidden picker for
                        the shopper's person photo (image only). Opened by a
                        try-on button when no photo is cached yet. */}
                    <input
                        ref={tryOnFileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleTryOnFileSelected}
                    />

                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                        placeholder={
                            chatTabInMaintenance
                                ? 'Chat unavailable'
                                : sessionEnded
                                    ? 'Conversation ended'
                                    : isListening
                                        ? 'Listening...'
                                        : 'Message...'
                        }
                        className={`flex-1 min-w-0 border rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed ${isListening
                            ? 'bg-red-50 border-red-200 placeholder-red-400'
                            : isDark
                                ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-200 text-gray-900 placeholder-gray-400'
                            }`}
                        style={{ '--tw-ring-color': activeBrandColor } as any}
                        disabled={(loading && handoffStatus !== 'active') || Date.now() < rateLimitUntil || sessionEnded || chatTabInMaintenance}
                    />

                    <button
                        onClick={() => handleSend()}
                        disabled={(loading && handoffStatus !== 'active') || (!input.trim() && pendingAttachments.length === 0 && !isListening) || Date.now() < rateLimitUntil || sessionEnded || chatTabInMaintenance}
                        className="p-2 rounded-full transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                        style={{ backgroundColor: activeTextColor, color: activeBrandColor }}
                    >
                        <Send size={18} />
                    </button>
                </div>
                <div className="text-center mt-2" style={{ color: activeTextColor, opacity: 0.7 }}>
                    <span className="text-[10px]">
                        Powered by{" "}
                        <a 
                            href="https://askprodigy.ai" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="underline"
                        >
                            AskProdigy AI
                        </a>
                    </span>
                </div>
            </div>}

            {/* Plan-CSATUIModal.md §B.2 — chat-tab CSAT modal. Mirrors
                the voice-tab render up above; rendered at the chat-mode
                root level so it overlays the message stream + input
                row after `conversation_ended` teardown. No TTS on the
                chat tab — visual stars + comment textarea only. */}
            {csatModalOpen && csatConversationId && (
                <CSATModal
                    conversationId={csatConversationId}
                    brandColor={activeBrandColor}
                    theme={theme}
                    useTTS={false}
                    onClose={() => {
                        setCsatModalOpen(false);
                        setCsatSubmittedForConv(csatConversationId);
                    }}
                />
            )}

            {/* Contract-WidgetEmailOTP.md — chat-tab OTP modal. Mirrors the
                voice-tab render above; overlays the message stream + input row.
                Opened purely by the `verification_required` flag on the WS
                payload. Dismissing costs nothing: the gate is enforced
                server-side inside the tool, so an unverified visitor simply
                keeps getting no order data. */}
            {verifyModalOpen && (verifySessionId || sessionId) && agentId && (
                <EmailVerificationModal
                    sessionId={verifySessionId || sessionId}
                    agentId={agentId}
                    brandColor={activeBrandColor}
                    theme={theme}
                    onVerified={handleVerified}
                    onClose={() => {
                        setVerifyModalOpen(false);
                        setVerifyDismissed(true);
                    }}
                    initialEmail={_verifyPrefill}
                    verifiedEmail={verifiedEmail}
                    reason={verifyReason}
                />
            )}
        </div>
    );
};

export default Widget;
